import {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  Fragment,
} from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import type {
  Task,
  User,
  Tag,
  RoomStatus,
  CustomColumn,
  CustomColumnValue,
  DashboardColumnSetting,
  DeadlineCardConfig,
  TaskTemplate,
  Reminder,
  ScoreMode,
  ScorecardSettings,
  ScorecardModeConfig,
} from "../types/types";
import type { TaskUpdateDto, TaskCreateDto } from "../hooks/useTodos";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";
import { MemberSelectionDialog } from "./MemberSelectionDialog.tsx";
import { ymdToUtcIso } from "../utils/date.ts";
import { isExcelFile, convertExcelToCsvFile } from "../utils/excelToCsv.ts";
import {
  PaletteColor,
  PaletteColorHex,
  PaletteColorBg,
} from "../types/PaletteColor.ts";
import { DashboardCustomColumnDialog } from "./DashboardCustomColumnDialog.tsx";
import { DashboardColumnSettingsDialog } from "./DashboardColumnSettingsDialog.tsx";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Switch from "@mui/material/Switch";
import { TaskDetailPanel } from "./TaskDetailPanel.tsx";
import { ConfirmDialog } from "./ConfirmDialog.tsx";
import { PanelResizeHandle } from "./PanelResizeHandle.tsx";
import { useResizablePanelWidth } from "../hooks/useResizablePanelWidth.ts";

// クイック作成プリセット
export interface DashboardPreset {
  id: string;
  name: string;
  priority: Task["priority"];
  status: string;
  tagIds: number[];
  createdHonbuId?: number;
}

interface Props {
  tasks: Task[];
  activeListId: string | null;
  roomId: number;
  honbuId: number;
  roomMembers: User[];
  availableTags: Tag[];
  roomStatuses: RoomStatus[];
  color: PaletteColor;
  isDarkMode: boolean;
  addTask: (
    taskData: TaskCreateDto,
    assigneeNames: string[],
  ) => Promise<string | null>;
  updateTask: (taskId: string, dto: TaskUpdateDto) => Promise<void>;
  updateTaskStatus: (taskId: string, status: Task["status"]) => Promise<void>;
  onDeleteTask: (taskId: string) => Promise<void>;
  refreshTodos: () => void;
  addSubtask?: (parentTaskId: string, title: string) => Promise<string | null>;
  addReminder?: (
    taskId: string,
    reminderDateTime: Date,
  ) => Promise<Reminder | null>;
  deleteReminder?: (reminderId: string | number) => Promise<boolean>;
  onManageTags?: () => void;
  forceReload?: () => void;
  onOpenRecurrenceManagement?: (
    selectedTaskId?: string,
    selectedRuleId?: number,
  ) => void;
  /** 全体設定でカスタム列が変更されるたびに増加し、列定義の再取得をトリガーする */
  columnsVersion?: number;
}

type SortKey = "title" | "status" | "endDate" | "priority" | string;
type SortDir = "asc" | "desc";
type ColumnMode = "common" | "personal";

const FIXED_COLUMN_LABELS: Record<string, string> = {
  status: "ステータス",
  assignee: "担当者",
  endDate: "期限",
  priority: "重要度",
  tags: "タグ",
};

// ダッシュボードテーブルの列幅（タイトル以外の各列）の既定値。
// ヘッダーの境界線をドラッグすると列ごとに上書きされ、localStorage に保存される。
const DEFAULT_COLUMN_WIDTHS: Record<string, number> = {
  status: 110,
  assignee: 120,
  endDate: 130,
  priority: 80,
  tags: 140,
};
const DEFAULT_CUSTOM_COLUMN_WIDTH = 140;
/** タスクタイトル列の幅を columnWidths で管理するためのキー */
const TITLE_COLUMN_KEY = "title";
/** タイトル列の最小幅 */
const TITLE_MIN_WIDTH = 220;
/** タイトル以外の列の最小幅 */
const MIN_COLUMN_WIDTH = 60;
/**
 * 右端「＋列追加」列の固定幅。
 * 本文行では詳細表示アイコンとメニューアイコンの2つが入るため、
 * それらが収まる幅にしておく（狭いと境界線が詳細アイコンより右にずれる）。
 */
const ADD_COLUMN_WIDTH = 72;

/** 列幅の localStorage キー（プロジェクトごとに保持する） */
function columnWidthsStorageKey(
  honbuId: number,
  roomId: number,
  listId: string | null,
): string {
  return `dashboardColumnWidths_${honbuId}_${roomId}_${listId ?? "none"}`;
}

/** 保存済みの列幅を読み込む（壊れた値は無視して既定幅にフォールバックする） */
function loadStoredColumnWidths(
  honbuId: number,
  roomId: number,
  listId: string | null,
): Record<string, number> {
  try {
    const raw = localStorage.getItem(
      columnWidthsStorageKey(honbuId, roomId, listId),
    );
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return {};
    const result: Record<string, number> = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (typeof value === "number" && Number.isFinite(value) && value > 0) {
        result[key] = value;
      }
    }
    return result;
  } catch {
    return {};
  }
}

const PRIORITY_CONFIG: Record<string, { circleColor: string; label: string }> =
  {
    高: { circleColor: "#EF4444", label: "高" },
    中: { circleColor: "#F97316", label: "中" },
    低: { circleColor: "#3B82F6", label: "低" },
    なし: { circleColor: "#9E9E9E", label: "なし" },
  };

// --- 日付ユーティリティ ---
function getJstToday(): string {
  const now = new Date();
  const jst = new Date(now.getTime() + 9 * 60 * 60 * 1000);
  return jst.toISOString().slice(0, 10);
}

function toDateStr(d: string | null | undefined): string {
  if (!d) return "";
  return d.slice(0, 10);
}

function daysUntil(endDateStr: string, today: string): number {
  const end = new Date(endDateStr.slice(0, 10));
  const tod = new Date(today);
  return Math.round((end.getTime() - tod.getTime()) / 86400000);
}

// --- 期限カード 固定1枚 ---
const DEADLINE_FIXED = {
  overdue: { label: "期限切れ", color: "#EF4444", bg: "#FEF2F2" },
} as const;
const DEADLINE_CUSTOM_COLOR = "#F97316";

// 期限カードのデフォルト色（DB 未設定・不正値のフォールバック）
const DEADLINE_DEFAULT_COLOR: PaletteColor = "Orange";

// カラーピッカーの選択肢（PaletteColor 全色）
const PALETTE_COLOR_KEYS = Object.keys(PaletteColorHex) as PaletteColor[];

// PaletteColor 名を検証して返す（DB に不正な値が入っていた場合はデフォルト色）
function toPaletteColor(value: string | null | undefined): PaletteColor {
  return value && value in PaletteColorHex
    ? (value as PaletteColor)
    : DEADLINE_DEFAULT_COLOR;
}

const PRIORITY_CARDS = [
  { key: "高", label: "高", color: "#EF4444", bg: "#FEF2F2" },
  { key: "中", label: "中", color: "#F97316", bg: "#FFF7ED" },
  { key: "低", label: "低", color: "#3B82F6", bg: "#EFF6FF" },
  { key: "なし", label: "なし", color: "#9E9E9E", bg: "#F3F4F6" },
] as const;

const PRIORITY_ORDER: Record<string, number> = {
  高: 0,
  中: 1,
  低: 2,
  なし: 3,
};

// 期限カードの一意キー（minDays/maxDays の組で識別。null は "n"）
function deadlineCardKey(card: DeadlineCardConfig): string {
  return `dl_${card.minDays ?? "n"}_${card.maxDays ?? "n"}`;
}

// 期限までの残日数がカードの範囲（minDays～maxDays、null=制限なし）に入るか
function matchesDeadlineCard(days: number, card: DeadlineCardConfig): boolean {
  if (card.minDays == null && card.maxDays == null) return false;
  if (card.minDays != null && days < card.minDays) return false;
  if (card.maxDays != null && days > card.maxDays) return false;
  return true;
}

// タスクが該当する期限カードキーをすべて返す（期間が重複するカードには重複してカウントする）。
// 期限なし・どのカードにも該当しない場合は空配列（どのカードにも集計しない）
function getDeadlineKeys(
  task: Task,
  today: string,
  deadlineCards: DeadlineCardConfig[],
): string[] {
  const end = toDateStr(task.endDate);
  if (!end) return [];
  const days = daysUntil(end, today);
  if (days < 0 && !task.completed) return ["overdue"];
  return deadlineCards
    .filter((card) => matchesDeadlineCard(days, card))
    .map(deadlineCardKey);
}

// --- スコアカード設定 ---
const DEFAULT_SCORECARD_SETTINGS: ScorecardSettings = {
  // デフォルト表示は「期限・ステータス・タグ」の3つ（非表示タブは常時固定表示）。
  // 重要度・担当者は「カテゴリを追加」から追加する
  visibleModes: [
    { mode: "deadline", label: "期限", isVisible: true, displayOrder: 0 },
    { mode: "status", label: "ステータス", isVisible: true, displayOrder: 1 },
    { mode: "hidden", label: "非表示", isVisible: true, displayOrder: 2 },
    { mode: "priority", label: "重要度", isVisible: false, displayOrder: 3 },
    { mode: "tags", label: "タグ", isVisible: true, displayOrder: 4 },
    { mode: "assignee", label: "担当者", isVisible: false, displayOrder: 5 },
  ],
  deadlineCards: [
    {
      minDays: null,
      maxDays: 0,
      label: "今日",
      color: DEADLINE_DEFAULT_COLOR,
      displayOrder: 0,
    },
    {
      minDays: null,
      maxDays: 7,
      label: "1週間以内",
      color: DEADLINE_DEFAULT_COLOR,
      displayOrder: 1,
    },
    {
      minDays: null,
      maxDays: 30,
      label: "1ヶ月以内",
      color: DEADLINE_DEFAULT_COLOR,
      displayOrder: 2,
    },
    {
      minDays: null,
      maxDays: 60,
      label: "2ヶ月以内",
      color: DEADLINE_DEFAULT_COLOR,
      displayOrder: 3,
    },
  ],
};

// 表示中カテゴリの先頭モードを返す（期限・ステータスも削除可能なため固定の
// "deadline" フォールバックは使えない）。表示カテゴリが1つもなければ非表示タブ
function getFirstVisibleMode(
  modes: ScorecardModeConfig[],
  roomId: number,
): ScoreMode {
  const visible = modes
    .filter(
      (m) =>
        m.isVisible &&
        m.mode !== "hidden" &&
        (m.mode !== "assignee" || roomId !== 0),
    )
    .sort((a, b) => a.displayOrder - b.displayOrder);
  return visible[0]?.mode ?? "hidden";
}

// --- 期間編集ポップオーバー ---
// TaskTableView の DueDatePopover と同じ操作（未設定 / 期間設定トグル + 開始日・終了日）。
// 外側クリックで保存してから閉じる。
const DashboardDuePopover = ({
  task,
  openUpward,
  isDarkMode,
  accentColor,
  cardBg,
  borderColor,
  textPrimary,
  textSecondary,
  onSave,
  onClose,
}: {
  task: Task;
  openUpward: boolean;
  isDarkMode: boolean;
  accentColor: string;
  cardBg: string;
  borderColor: string;
  textPrimary: string;
  textSecondary: string;
  onSave: (updates: { startDate?: string; endDate?: string }) => void;
  onClose: () => void;
}) => {
  const startStr = toDateStr(task.startDate);
  const endStr = toDateStr(task.endDate);
  const [isRange, setIsRange] = useState(
    !!startStr && !!endStr && startStr !== endStr,
  );
  const [start, setStart] = useState(startStr || endStr || "");
  const [end, setEnd] = useState(endStr || startStr || "");
  const ref = useRef<HTMLDivElement>(null);

  // 確定（保存）して閉じる。開始日が未入力（yyyy/mm/dd 表示のまま）なら未設定として保存する。
  const handleCloseAndSave = useCallback(() => {
    if (!start) {
      onSave({ startDate: undefined, endDate: undefined });
    } else {
      const startISO = ymdToUtcIso(start);
      const endISO = isRange && end ? ymdToUtcIso(end) : startISO;
      onSave({ startDate: startISO, endDate: endISO });
    }
    onClose();
  }, [isRange, start, end, onSave, onClose]);

  // 未設定にする（入力値をリセットして yyyy/mm/dd 表示に戻す）
  const handleReset = useCallback(() => {
    setStart("");
    setEnd("");
    setIsRange(false);
  }, []);

  // 外側クリックで保存して閉じる
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        handleCloseAndSave();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [handleCloseAndSave]);

  const inputClass =
    "w-full p-1 rounded mt-1 text-sm border focus:outline-none";
  const inputStyle: React.CSSProperties = {
    borderColor,
    background: isDarkMode ? "#2a2a2a" : "#fff",
    color: textPrimary,
  };

  return (
    <div
      ref={ref}
      className={`absolute left-2 z-40 rounded-lg shadow-lg border p-3 w-56 ${openUpward ? "bottom-full mb-1" : "top-8"}`}
      style={{ background: cardBg, borderColor }}
      // 親セル（td）がクリックで編集トグルするため、ポップオーバー内のクリックは伝播させない
      onClick={(e) => e.stopPropagation()}
    >
      <div className="space-y-2.5">
        <label className="flex items-center justify-between cursor-pointer">
          <span className="text-sm" style={{ color: textPrimary }}>
            期間を設定
          </span>
          <Switch
            size="small"
            checked={isRange}
            onChange={(e) => setIsRange(e.target.checked)}
          />
        </label>
        <div>
          <label className="text-xs" style={{ color: textSecondary }}>
            {isRange ? "開始日" : "日付"}
          </label>
          <input
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            className={inputClass}
            style={inputStyle}
          />
        </div>
        {isRange && (
          <div>
            <label className="text-xs" style={{ color: textSecondary }}>
              終了日
            </label>
            <input
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              className={inputClass}
              style={inputStyle}
            />
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button
            onClick={handleReset}
            className="px-3 py-1 rounded text-xs font-medium border"
            style={{
              borderColor,
              color: textSecondary,
              background: "transparent",
            }}
          >
            未設定にする
          </button>
          <button
            onClick={handleCloseAndSave}
            className="px-3 py-1 rounded text-xs font-medium text-white"
            style={{ background: accentColor }}
          >
            完了
          </button>
        </div>
      </div>
    </div>
  );
};

// --- サブタスクを含めフラット化 ---
function flattenTasks(tasks: Task[]): Task[] {
  const result: Task[] = [];
  const traverse = (list: Task[]) => {
    for (const t of list) {
      result.push(t);
      if (t.childTasks && t.childTasks.length > 0) traverse(t.childTasks);
    }
  };
  traverse(tasks);
  return result;
}

export const DashboardView = ({
  tasks,
  activeListId,
  roomId,
  honbuId,
  roomMembers,
  availableTags,
  roomStatuses,
  color,
  isDarkMode,
  addTask,
  updateTask,
  updateTaskStatus,
  onDeleteTask,
  refreshTodos,
  addSubtask,
  addReminder,
  deleteReminder,
  onManageTags,
  forceReload,
  onOpenRecurrenceManagement,
  columnsVersion,
}: Props) => {
  const accentColor = PaletteColorHex[color];
  const jstToday = useMemo(() => getJstToday(), []);

  // --- State ---
  const [commonScorecardSettings, setCommonScorecardSettings] =
    useState<ScorecardSettings>(
      JSON.parse(JSON.stringify(DEFAULT_SCORECARD_SETTINGS)),
    );
  const [personalScorecardSettings, setPersonalScorecardSettings] =
    useState<ScorecardSettings>(
      JSON.parse(JSON.stringify(DEFAULT_SCORECARD_SETTINGS)),
    );
  const [scorecardMode, setScorecardMode] = useState<ColumnMode>(() => {
    if (!activeListId) return "common";
    return (
      (localStorage.getItem(
        `dashboardScorecardMode_${honbuId}_${roomId}_${activeListId}`,
      ) as ColumnMode) ?? "common"
    );
  });
  const scorecardSettings =
    scorecardMode === "common"
      ? commonScorecardSettings
      : personalScorecardSettings;
  // スコアカード設定のロード完了フラグ（ロード前のデフォルト設定でスコアモードを
  // 誤って検証・リセットしないためのガード）
  const [isScorecardLoaded, setIsScorecardLoaded] = useState(false);
  // --- スコアカード インライン編集 ---
  const [isScorecardEditMode, setIsScorecardEditMode] = useState(false);
  const [scorecardDraft, setScorecardDraft] =
    useState<ScorecardSettings | null>(null);
  const [showAddCategoryMenu, setShowAddCategoryMenu] = useState(false);
  const [editingCardIdx, setEditingCardIdx] = useState<number | null>(null);
  // 期限カードの色選択ポップオーバー（編集中カードの色丸押下で開く）
  const [isCardColorPickerOpen, setIsCardColorPickerOpen] = useState(false);
  const cardColorPickerRef = useRef<HTMLDivElement>(null);
  // 編集中は draft を、それ以外は保存済み設定を参照する
  const effectiveScorecardSettings =
    isScorecardEditMode && scorecardDraft ? scorecardDraft : scorecardSettings;
  // タグ/担当者カードの表示件数（モードキーごと）
  const [cardVisibleCount, setCardVisibleCount] = useState<
    Record<string, number>
  >({});

  const [scoreMode, setScoreMode] = useState<ScoreMode>(() => {
    if (!activeListId) return "deadline";
    return (
      (localStorage.getItem(
        `dashboardScoreMode_${honbuId}_${roomId}_${activeListId}`,
      ) as ScoreMode) ?? "deadline"
    );
  });
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const [customColumns, setCustomColumns] = useState<CustomColumn[]>([]);
  const [customColumnValues, setCustomColumnValues] = useState<
    CustomColumnValue[]
  >([]);
  const [showColumnDialog, setShowColumnDialog] = useState(false);
  const [columnMode, setColumnMode] = useState<ColumnMode>(() => {
    if (!activeListId) return "common";
    return (
      (localStorage.getItem(
        `dashboardColumnMode_${honbuId}_${roomId}_${activeListId}`,
      ) as ColumnMode) ?? "common"
    );
  });
  const [commonColumnSettings, setCommonColumnSettings] = useState<
    DashboardColumnSetting[]
  >([]);
  const [personalColumnSettings, setPersonalColumnSettings] = useState<
    DashboardColumnSetting[]
  >([]);
  const [showColumnSettingsDialog, setShowColumnSettingsDialog] =
    useState(false);
  // 列幅の手動指定（キー → px）。ヘッダー境界のドラッグで更新し、
  // プロジェクトごとに localStorage へ保存する。未指定の列は既定幅を使う。
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() =>
    loadStoredColumnWidths(honbuId, roomId, activeListId),
  );
  // ドラッグ中の列キー（境界線のハイライト用）
  const [resizingColumnKey, setResizingColumnKey] = useState<string | null>(
    null,
  );
  const [sortKey, setSortKey] = useState<SortKey>("endDate");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [editingStatusTaskId, setEditingStatusTaskId] = useState<string | null>(
    null,
  );
  const [editingCell, setEditingCell] = useState<{
    taskId: string;
    columnId: number;
  } | null>(null);
  const [cellDraft, setCellDraft] = useState("");
  const [loadingCellKey, setLoadingCellKey] = useState<string | null>(null);
  // インライン編集用 state
  type FieldEdit = "title" | "endDate" | "priority" | "tags";
  const [fieldEdit, setFieldEdit] = useState<{
    taskId: string;
    field: FieldEdit;
  } | null>(null);
  const [titleDraft, setTitleDraft] = useState("");
  const [memberDialogTask, setMemberDialogTask] = useState<Task | null>(null);
  const [dropdownOpenUpward, setDropdownOpenUpward] = useState(false);
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const cellInputRef = useRef<HTMLInputElement>(null);
  const csvFileInputRef = useRef<HTMLInputElement>(null);
  const newTaskInputRef = useRef<HTMLInputElement>(null);

  // --- 三点メニュー・詳細パネル ---
  const [openMenuTaskId, setOpenMenuTaskId] = useState<string | null>(null);
  const [detailTask, setDetailTask] = useState<Task | null>(null);
  // 詳細パネルのピン留め状態（プロジェクトごとに localStorage 保存・基本 OFF）。
  // ON のときはオーバーレイではなくテーブル横に固定表示する。
  const [isDetailPinned, setIsDetailPinned] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    message: string;
    onConfirm: () => void;
  }>({ isOpen: false, message: "", onConfirm: () => {} });
  const [addingSubtaskForTaskId, setAddingSubtaskForTaskId] = useState<
    string | null
  >(null);
  const [newSubtaskTitle, setNewSubtaskTitle] = useState("");
  const [subtaskTemplates, setSubtaskTemplates] = useState<TaskTemplate[]>([]);
  const [showSubtaskTemplatesForTaskId, setShowSubtaskTemplatesForTaskId] =
    useState<string | null>(null);
  const menuPopoverRef = useRef<HTMLDivElement>(null);
  const subtaskInputRef = useRef<HTMLInputElement>(null);

  // --- 新規タスク作成 ---
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [selectedPreset, setSelectedPreset] = useState<DashboardPreset | null>(
    null,
  );
  const [showPresetDropdown, setShowPresetDropdown] = useState(false);
  const [presets, setPresets] = useState<DashboardPreset[]>([]);
  const [showAddPresetForm, setShowAddPresetForm] = useState(false);
  const [draftPreset, setDraftPreset] = useState<Omit<DashboardPreset, "id">>({
    name: "",
    priority: "なし",
    status: "未対応",
    tagIds: [],
  });

  // プリセット読み込み（リスト切り替え時にAPIから取得）
  useEffect(() => {
    setSelectedPreset(null);
    setPresets([]);
    if (!activeListId || activeListId === "0") return;
    const load = async () => {
      try {
        const res = await fetch(
          `/api/todolists/${activeListId}/dashboard-presets`,
        );
        if (res.ok) setPresets(await res.json());
      } catch {
        /* ignore */
      }
    };
    load();
  }, [activeListId]);

  const handleCreateTask = useCallback(
    async (options?: { refocus?: boolean }) => {
      const title = newTaskTitle.trim();
      if (!title || !activeListId) return;
      setNewTaskTitle("");
      const taskData: TaskCreateDto = {
        title,
        todoListId: parseInt(activeListId, 10),
        status: (selectedPreset?.status as Task["status"]) || "未対応",
        priority: selectedPreset?.priority || "なし",
        tagIds: selectedPreset?.tagIds || [],
        notification: false,
        createdHonbuId: honbuId,
        roomId,
        type: "single",
        assigneeHonbuIds: [],
        requesterHonbuIds: [],
      };
      await addTask(taskData, []);
      if (options?.refocus)
        requestAnimationFrame(() => newTaskInputRef.current?.focus());
    },
    [newTaskTitle, activeListId, selectedPreset, honbuId, roomId, addTask],
  );

  const handleCreateTaskFromPreset = useCallback(
    async (preset: DashboardPreset) => {
      if (!activeListId) return;
      setShowPresetDropdown(false);
      const taskData: TaskCreateDto = {
        title: preset.name,
        todoListId: parseInt(activeListId, 10),
        status: (preset.status as Task["status"]) || "未対応",
        priority: preset.priority || "なし",
        tagIds: preset.tagIds || [],
        notification: false,
        createdHonbuId: honbuId,
        roomId,
        type: "single",
        assigneeHonbuIds: [],
        requesterHonbuIds: [],
      };
      await addTask(taskData, []);
      requestAnimationFrame(() => newTaskInputRef.current?.focus());
    },
    [activeListId, honbuId, roomId, addTask],
  );

  const handleSavePreset = useCallback(async () => {
    if (!draftPreset.name.trim() || !activeListId) return;
    try {
      const res = await fetch(
        `/api/todolists/${activeListId}/dashboard-presets?honbuId=${honbuId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: draftPreset.name.trim(),
            priority: draftPreset.priority,
            status: draftPreset.status,
            tagIds: draftPreset.tagIds,
          }),
        },
      );
      if (!res.ok) return;
      const created: DashboardPreset = await res.json();
      setPresets((prev) => [...prev, created]);
      setDraftPreset({
        name: "",
        priority: "なし",
        status: "未対応",
        tagIds: [],
      });
      setShowAddPresetForm(false);
    } catch {
      /* ignore */
    }
  }, [draftPreset, activeListId, honbuId]);

  const handleDeletePreset = useCallback(
    async (id: string) => {
      try {
        const res = await fetch(
          `/api/dashboard-presets/${id}?honbuId=${honbuId}`,
          { method: "DELETE" },
        );
        if (res.ok) {
          setPresets((prev) => prev.filter((p) => p.id !== id));
          if (selectedPreset?.id === id) setSelectedPreset(null);
        }
      } catch {
        /* ignore */
      }
    },
    [honbuId, selectedPreset],
  );

  const [showCsvImportDialog, setShowCsvImportDialog] = useState(false);
  // テンプレートDL時に期限を開始日・終了日の2列に分けるか（OFF=期限1列）
  const [splitPeriod, setSplitPeriod] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState<{
    successCount: number;
    errorCount: number;
    warningCount: number;
    errors: { row: number; message: string }[];
  } | null>(null);

  // --- データフェッチ ---
  useEffect(() => {
    if (!activeListId || activeListId === "0") return;
    const loadColumns = async () => {
      try {
        const res = await fetch(
          `/api/todolists/${activeListId}/custom-columns`,
        );
        if (res.ok) setCustomColumns(await res.json());
      } catch {
        // ignore
      }
    };
    loadColumns();
  }, [activeListId, columnsVersion]);

  const reloadCustomColumnValues = useCallback(async () => {
    if (!activeListId || activeListId === "0") return;
    try {
      const res = await fetch(
        `/api/todolists/${activeListId}/custom-column-values`,
      );
      if (res.ok) setCustomColumnValues(await res.json());
    } catch {
      // ignore
    }
  }, [activeListId]);

  useEffect(() => {
    reloadCustomColumnValues();
  }, [reloadCustomColumnValues]);

  // 列表示設定のロード
  useEffect(() => {
    if (!activeListId || activeListId === "0") return;
    const load = async (isCommon: boolean) => {
      try {
        const res = await fetch(
          `/api/todolists/${activeListId}/dashboard-columns?honbuId=${honbuId}&isCommon=${isCommon}`,
        );
        if (res.ok) {
          const data: DashboardColumnSetting[] = await res.json();
          if (isCommon) setCommonColumnSettings(data);
          else setPersonalColumnSettings(data);
        }
      } catch {
        // ignore
      }
    };
    load(true);
    load(false);
  }, [activeListId, honbuId]);

  // スコアカード設定のロード
  useEffect(() => {
    setIsScorecardLoaded(false);
    if (!activeListId || activeListId === "0") return;
    const load = async (isCommon: boolean) => {
      try {
        const res = await fetch(
          `/api/todolists/${activeListId}/scorecard-settings?honbuId=${honbuId}&isCommon=${isCommon}`,
        );
        if (!res.ok) return;
        const data = await res.json();
        const settings: ScorecardSettings = {
          visibleModes:
            data.modes?.length > 0
              ? data.modes.map(
                  (m: {
                    scoreMode: ScoreMode;
                    label: string;
                    isVisible: boolean;
                    displayOrder: number;
                  }) => ({
                    mode: m.scoreMode,
                    label: m.label,
                    isVisible: m.isVisible,
                    displayOrder: m.displayOrder,
                  }),
                )
              : DEFAULT_SCORECARD_SETTINGS.visibleModes,
          deadlineCards:
            data.deadlineCards?.length > 0
              ? data.deadlineCards.map(
                  (c: {
                    minDays: number | null;
                    maxDays: number | null;
                    label: string;
                    color?: string | null;
                    displayOrder: number;
                  }) => ({
                    minDays: c.minDays ?? null,
                    maxDays: c.maxDays ?? null,
                    label: c.label,
                    color: toPaletteColor(c.color),
                    displayOrder: c.displayOrder,
                  }),
                )
              : DEFAULT_SCORECARD_SETTINGS.deadlineCards,
        };
        if (isCommon) setCommonScorecardSettings(settings);
        else setPersonalScorecardSettings(settings);
      } catch {
        // ignore
      }
    };
    Promise.all([load(true), load(false)]).then(() =>
      setIsScorecardLoaded(true),
    );
  }, [activeListId, honbuId]);

  // プロジェクト切り替え時に保存済みの共通/個人・スコアモード・列表示モードを反映
  // （useState 初期化子は初回のみ実行されるため、リスト変更時に再読込が必要）
  useEffect(() => {
    if (!activeListId) return;
    const savedScorecardMode = localStorage.getItem(
      `dashboardScorecardMode_${honbuId}_${roomId}_${activeListId}`,
    ) as ColumnMode | null;
    setScorecardMode(savedScorecardMode ?? "common");
    const savedScoreMode = localStorage.getItem(
      `dashboardScoreMode_${honbuId}_${roomId}_${activeListId}`,
    ) as ScoreMode | null;
    setScoreMode(savedScoreMode ?? "deadline");
    const savedColumnMode = localStorage.getItem(
      `dashboardColumnMode_${honbuId}_${roomId}_${activeListId}`,
    ) as ColumnMode | null;
    setColumnMode(savedColumnMode ?? "common");
    // 詳細パネルのピン留め状態を復元（未保存＝基本 OFF）
    const savedPinned = localStorage.getItem(
      `dashboardDetailPinned_${honbuId}_${roomId}_${activeListId}`,
    );
    setIsDetailPinned(savedPinned === "1");
    // 列幅もプロジェクトごとに保存しているため切り替え時に読み直す
    setColumnWidths(loadStoredColumnWidths(honbuId, roomId, activeListId));
    setSelectedCard(null);
    // 編集モードはプロジェクト切り替えで解除する
    setIsScorecardEditMode(false);
    setScorecardDraft(null);
    setEditingCardIdx(null);
    setShowAddCategoryMenu(false);
  }, [activeListId, honbuId, roomId]);

  // 現在のスコアモードが設定上非表示（期限・ステータスも削除可能）の場合は
  // 先頭の表示カテゴリに切り替える（localStorage 復元値が削除済みタブを指すケース）
  useEffect(() => {
    if (!isScorecardLoaded || isScorecardEditMode) return;
    const visible = scorecardSettings.visibleModes.some(
      (m) =>
        m.mode === scoreMode &&
        m.isVisible &&
        (m.mode !== "assignee" || roomId !== 0),
    );
    if (!visible) {
      setScoreMode(getFirstVisibleMode(scorecardSettings.visibleModes, roomId));
      setSelectedCard(null);
    }
  }, [
    scorecardSettings,
    scoreMode,
    isScorecardEditMode,
    isScorecardLoaded,
    roomId,
  ]);

  // 期限カードの色選択ポップオーバーは編集対象カードの切替・編集モード終了で閉じる
  useEffect(() => {
    setIsCardColorPickerOpen(false);
  }, [editingCardIdx, isScorecardEditMode]);

  // 色選択ポップオーバーの外側クリックで閉じる
  useEffect(() => {
    if (!isCardColorPickerOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        cardColorPickerRef.current &&
        !cardColorPickerRef.current.contains(e.target as Node)
      ) {
        setIsCardColorPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isCardColorPickerOpen]);

  // 詳細パネルのピン留め切り替え（プロジェクトごとに localStorage 保存）
  const handleToggleDetailPin = useCallback(() => {
    setIsDetailPinned((prev) => {
      const next = !prev;
      if (activeListId) {
        localStorage.setItem(
          `dashboardDetailPinned_${honbuId}_${roomId}_${activeListId}`,
          next ? "1" : "0",
        );
      }
      return next;
    });
  }, [activeListId, honbuId, roomId]);

  // スコアカードモード切り替え
  const handleScorecardModeChange = useCallback(
    (mode: ColumnMode) => {
      setScorecardMode(mode);
      // 編集中に保存先を切り替えた場合は、その保存先の設定で draft を作り直す
      if (isScorecardEditMode) {
        const target =
          mode === "common"
            ? commonScorecardSettings
            : personalScorecardSettings;
        setScorecardDraft(JSON.parse(JSON.stringify(target)));
        setEditingCardIdx(null);
        setShowAddCategoryMenu(false);
      }
      if (activeListId) {
        localStorage.setItem(
          `dashboardScorecardMode_${honbuId}_${roomId}_${activeListId}`,
          mode,
        );
      }
    },
    [
      activeListId,
      honbuId,
      roomId,
      isScorecardEditMode,
      commonScorecardSettings,
      personalScorecardSettings,
    ],
  );

  // --- スコアカード編集: addable カテゴリ定義 ---
  const optionalModeDefs = useMemo<ScorecardModeConfig[]>(
    () => [
      { mode: "deadline", label: "期限", isVisible: false, displayOrder: 0 },
      {
        mode: "status",
        label: "ステータス",
        isVisible: false,
        displayOrder: 1,
      },
      { mode: "priority", label: "重要度", isVisible: false, displayOrder: 3 },
      { mode: "tags", label: "タグ", isVisible: false, displayOrder: 4 },
      ...(roomId !== 0
        ? [
            {
              mode: "assignee" as ScoreMode,
              label: "担当者",
              isVisible: false,
              displayOrder: 5,
            },
          ]
        : []),
      ...customColumns
        .filter((c) => c.columnType === "select")
        .map((col, i) => ({
          mode: `custom_${col.customColumnId}` as ScoreMode,
          label: col.columnName,
          isVisible: false,
          displayOrder: 6 + i,
        })),
    ],
    [roomId, customColumns],
  );

  // 追加可能なカテゴリ（draft にまだ表示されていないもの）
  const addableModes = useMemo(() => {
    const draft = effectiveScorecardSettings;
    return optionalModeDefs.filter(
      (def) =>
        !draft.visibleModes.some((m) => m.mode === def.mode && m.isVisible),
    );
  }, [optionalModeDefs, effectiveScorecardSettings]);

  // --- スコアカード編集: 入退場 ---
  const enterScorecardEditMode = useCallback(() => {
    setScorecardDraft(JSON.parse(JSON.stringify(scorecardSettings)));
    setIsScorecardEditMode(true);
    setEditingCardIdx(null);
    setShowAddCategoryMenu(false);
  }, [scorecardSettings]);

  // --- スコアカード編集: カテゴリ追加 ---
  const handleAddCategory = useCallback((target: ScorecardModeConfig) => {
    setScorecardDraft((prev) => {
      if (!prev) return prev;
      const existing = prev.visibleModes.find((m) => m.mode === target.mode);
      if (existing) {
        return {
          ...prev,
          visibleModes: prev.visibleModes.map((m) =>
            m.mode === target.mode ? { ...m, isVisible: true } : m,
          ),
        };
      }
      return {
        ...prev,
        visibleModes: [...prev.visibleModes, { ...target, isVisible: true }],
      };
    });
  }, []);

  // --- スコアカード編集: カテゴリ削除 ---
  const handleRemoveCategory = useCallback(
    (mode: ScoreMode) => {
      const nextModes = (scorecardDraft?.visibleModes ?? []).map((m) =>
        m.mode === mode ? { ...m, isVisible: false } : m,
      );
      setScorecardDraft((prev) =>
        prev
          ? {
              ...prev,
              visibleModes: prev.visibleModes.map((m) =>
                m.mode === mode ? { ...m, isVisible: false } : m,
              ),
            }
          : prev,
      );
      // 削除したカテゴリを表示中だった場合は残りの先頭カテゴリに戻す
      if (scoreMode === mode) {
        const fallback = getFirstVisibleMode(nextModes, roomId);
        setScoreMode(fallback);
        setSelectedCard(null);
        if (activeListId) {
          localStorage.setItem(
            `dashboardScoreMode_${honbuId}_${roomId}_${activeListId}`,
            fallback,
          );
        }
      }
    },
    [scorecardDraft, scoreMode, activeListId, honbuId, roomId],
  );

  // --- スコアカード編集: 期限カード（●～●日）操作 ---
  const handleAddDeadlineCard = useCallback(() => {
    setScorecardDraft((prev) => {
      if (!prev) return prev;
      // 既存カードの上限のうち最大値 + 1 を初期値にする（上限なしカードは無視）
      const maxUsed =
        prev.deadlineCards.length > 0
          ? Math.max(
              ...prev.deadlineCards.map((c) => c.maxDays ?? c.minDays ?? -1),
            )
          : -1;
      const nextMax = maxUsed + 1;
      return {
        ...prev,
        deadlineCards: [
          ...prev.deadlineCards,
          {
            minDays: null,
            maxDays: nextMax,
            label: `${nextMax}日以内`,
            color: DEADLINE_DEFAULT_COLOR,
            displayOrder: prev.deadlineCards.length,
          },
        ],
      };
    });
  }, []);

  const handleUpdateDeadlineCardLabel = useCallback(
    (index: number, label: string) => {
      setScorecardDraft((prev) =>
        prev
          ? {
              ...prev,
              deadlineCards: prev.deadlineCards.map((c, i) =>
                i === index ? { ...c, label } : c,
              ),
            }
          : prev,
      );
    },
    [],
  );

  const handleUpdateDeadlineCardColor = useCallback(
    (index: number, cardColor: PaletteColor) => {
      setScorecardDraft((prev) =>
        prev
          ? {
              ...prev,
              deadlineCards: prev.deadlineCards.map((c, i) =>
                i === index ? { ...c, color: cardColor } : c,
              ),
            }
          : prev,
      );
      setIsCardColorPickerOpen(false);
    },
    [],
  );

  // 範囲（開始/終了日数）の更新。空文字は「制限なし」（null）
  const handleUpdateDeadlineCardRange = useCallback(
    (index: number, field: "minDays" | "maxDays", raw: string) => {
      let value: number | null;
      if (raw === "") {
        value = null;
      } else {
        const n = parseInt(raw, 10);
        if (isNaN(n) || n < 0 || n > 365) return;
        value = n;
      }
      setScorecardDraft((prev) =>
        prev
          ? {
              ...prev,
              deadlineCards: prev.deadlineCards.map((c, i) =>
                i === index ? { ...c, [field]: value } : c,
              ),
            }
          : prev,
      );
    },
    [],
  );

  const handleRemoveDeadlineCard = useCallback((index: number) => {
    setScorecardDraft((prev) =>
      prev
        ? {
            ...prev,
            deadlineCards: prev.deadlineCards.filter((_, i) => i !== index),
          }
        : prev,
    );
    setEditingCardIdx(null);
  }, []);

  // 期限カードのドラッグ&ドロップ並び替え（編集モードのみ）
  const handleDeadlineCardDragEnd = useCallback((result: DropResult) => {
    if (!result.destination) return;
    const from = result.source.index;
    const to = result.destination.index;
    if (from === to) return;
    setScorecardDraft((prev) => {
      if (!prev) return prev;
      const cards = [...prev.deadlineCards];
      const [moved] = cards.splice(from, 1);
      cards.splice(to, 0, moved);
      return {
        ...prev,
        deadlineCards: cards.map((c, i) => ({ ...c, displayOrder: i })),
      };
    });
    // インライン編集中のカードがあれば、移動後のインデックスに追従させる
    setEditingCardIdx((prev) => {
      if (prev == null) return prev;
      if (prev === from) return to;
      if (from < prev && to >= prev) return prev - 1;
      if (from > prev && to <= prev) return prev + 1;
      return prev;
    });
  }, []);

  // --- スコアカード編集: 保存して編集モード終了 ---
  const handleSaveScorecard = useCallback(async () => {
    if (!scorecardDraft) {
      setIsScorecardEditMode(false);
      return;
    }
    const isCommon = scorecardMode === "common";
    // 期限カードの正規化: 範囲が両方未入力のカードは除外し、開始 > 終了は入れ替え、
    // displayOrder を配列順（=表示順）で振り直す
    const normalizedDeadlineCards = scorecardDraft.deadlineCards
      .filter((c) => c.minDays != null || c.maxDays != null)
      .map((c) =>
        c.minDays != null && c.maxDays != null && c.minDays > c.maxDays
          ? { ...c, minDays: c.maxDays, maxDays: c.minDays }
          : c,
      )
      .map((c, i) => ({ ...c, displayOrder: i }));
    // select型カスタム列のカスタムモードを設定に追加・同期
    const selectCols = customColumns.filter((c) => c.columnType === "select");
    // 期限・ステータスは表示位置固定だが、非表示（削除）は draft の状態を尊重する
    const draftDeadline = scorecardDraft.visibleModes.find(
      (m) => m.mode === "deadline",
    );
    const draftStatus = scorecardDraft.visibleModes.find(
      (m) => m.mode === "status",
    );
    const merged: ScorecardSettings = {
      ...scorecardDraft,
      deadlineCards: normalizedDeadlineCards,
      visibleModes: [
        {
          mode: "deadline",
          label: "期限",
          isVisible: draftDeadline?.isVisible ?? true,
          displayOrder: 0,
        },
        {
          mode: "status",
          label: "ステータス",
          isVisible: draftStatus?.isVisible ?? true,
          displayOrder: 1,
        },
        { mode: "hidden", label: "非表示", isVisible: true, displayOrder: 2 },
        ...scorecardDraft.visibleModes.filter(
          (m) =>
            m.mode !== "deadline" && m.mode !== "status" && m.mode !== "hidden",
        ),
        ...selectCols
          .filter(
            (col) =>
              !scorecardDraft.visibleModes.some(
                (m) => m.mode === `custom_${col.customColumnId}`,
              ),
          )
          .map((col, i) => ({
            mode: `custom_${col.customColumnId}` as const,
            label: col.columnName,
            isVisible: false,
            displayOrder: 100 + i,
          })),
      ],
    };

    // DB に保存
    if (activeListId && activeListId !== "0") {
      try {
        await fetch(
          `/api/todolists/${activeListId}/scorecard-settings?honbuId=${honbuId}&isCommon=${isCommon}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              modes: merged.visibleModes.map((m) => ({
                scoreMode: m.mode,
                label: m.label,
                isVisible: m.isVisible,
                displayOrder: m.displayOrder,
              })),
              deadlineCards: merged.deadlineCards,
            }),
          },
        );
      } catch {
        /* ignore */
      }
    }

    // ローカル state 更新
    if (isCommon) setCommonScorecardSettings(merged);
    else setPersonalScorecardSettings(merged);

    // 現在表示中のモードが非表示になっていたら残りの先頭カテゴリに戻す
    const stillVisible = merged.visibleModes.some(
      (m) => m.mode === scoreMode && m.isVisible,
    );
    if (!stillVisible) {
      const fallback = getFirstVisibleMode(merged.visibleModes, roomId);
      setScoreMode(fallback);
      setSelectedCard(null);
      if (activeListId) {
        localStorage.setItem(
          `dashboardScoreMode_${honbuId}_${roomId}_${activeListId}`,
          fallback,
        );
      }
    }

    // 編集モード終了
    setIsScorecardEditMode(false);
    setScorecardDraft(null);
    setEditingCardIdx(null);
    setShowAddCategoryMenu(false);
  }, [
    scorecardDraft,
    scorecardMode,
    customColumns,
    activeListId,
    honbuId,
    roomId,
    scoreMode,
  ]);

  // 列モード切り替え
  const handleColumnModeChange = useCallback(
    (mode: ColumnMode) => {
      setColumnMode(mode);
      if (activeListId) {
        localStorage.setItem(
          `dashboardColumnMode_${honbuId}_${roomId}_${activeListId}`,
          mode,
        );
      }
    },
    [activeListId, honbuId, roomId],
  );

  // 列幅取得（手動指定 → 固定の既定値 → カスタム列既定値）
  const getColumnWidth = useCallback(
    (key: string): number =>
      columnWidths[key] ??
      DEFAULT_COLUMN_WIDTHS[key] ??
      DEFAULT_CUSTOM_COLUMN_WIDTH,
    [columnWidths],
  );

  // 設定保存後に再ロードしてダイアログを閉じる
  const handleColumnSettingsSaved = useCallback(async () => {
    if (!activeListId || activeListId === "0") return;
    const reload = async (isCommon: boolean) => {
      try {
        const res = await fetch(
          `/api/todolists/${activeListId}/dashboard-columns?honbuId=${honbuId}&isCommon=${isCommon}`,
        );
        if (res.ok) {
          const data: DashboardColumnSetting[] = await res.json();
          if (isCommon) setCommonColumnSettings(data);
          else setPersonalColumnSettings(data);
        }
      } catch {
        // ignore
      }
    };
    await Promise.all([reload(true), reload(false)]);
    setShowColumnSettingsDialog(false);
  }, [activeListId, honbuId]);

  // アクティブな列キー（表示順・表示可否を反映）
  const activeColumnKeys = useMemo(() => {
    const settings =
      columnMode === "common" ? commonColumnSettings : personalColumnSettings;
    const defaultFixed = [
      "status",
      ...(roomId !== 0 ? ["assignee"] : []),
      "endDate",
      "priority",
      "tags",
    ];
    const defaultCustom = customColumns.map(
      (c) => `custom_${c.customColumnId}`,
    );

    if (settings.length === 0) return [...defaultFixed, ...defaultCustom];

    const allKnownKeys = new Set([...defaultFixed, ...defaultCustom]);
    const ordered = settings
      .filter((s) => s.isVisible && allKnownKeys.has(s.columnKey))
      .filter((s) => s.columnKey !== "assignee" || roomId !== 0)
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .map((s) => s.columnKey);

    // 設定に一切含まれていない列を末尾に追加する。
    // 対象: 新規カスタム列、および設定保存前から存在した固定列（タグ等）。
    // 明示的に非表示にした列（設定に存在し isVisible=false）は再追加しない。
    const settingsKeys = new Set(settings.map((s) => s.columnKey));
    const unsaved = [...defaultFixed, ...defaultCustom].filter(
      (k) => !settingsKeys.has(k) && (k !== "assignee" || roomId !== 0),
    );
    return [...ordered, ...unsaved];
  }, [
    columnMode,
    commonColumnSettings,
    personalColumnSettings,
    customColumns,
    roomId,
  ]);

  // 動的列（タイトル・＋列を除く）の幅合計
  const dynamicColumnsWidth = useMemo(
    () => activeColumnKeys.reduce((sum, k) => sum + getColumnWidth(k), 0),
    [activeColumnKeys, getColumnWidth],
  );

  // テーブルのスクロール領域の表示幅。タイトル列の自動幅算出に使う。
  const [tableViewportWidth, setTableViewportWidth] = useState(0);
  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    const update = () => setTableViewportWidth(el.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // タイトル列の幅。手動指定がなければ表示領域の残り幅を吸収して横幅いっぱいに広げる。
  const titleColumnWidth = useMemo(() => {
    const manual = columnWidths[TITLE_COLUMN_KEY];
    if (manual) return Math.max(TITLE_MIN_WIDTH, manual);
    const rest = tableViewportWidth - dynamicColumnsWidth - ADD_COLUMN_WIDTH;
    return Math.max(TITLE_MIN_WIDTH, rest);
  }, [columnWidths, tableViewportWidth, dynamicColumnsWidth]);

  // テーブル全体の幅 = タイトル列 + 各列幅 + ＋列ボタン分。
  // 列幅の合計をそのままテーブル幅にすることで、列を狭めれば表全体も縮み、
  // 広げれば表全体が広がって横スクロールする挙動になる。
  const tableTotalWidth =
    titleColumnWidth + dynamicColumnsWidth + ADD_COLUMN_WIDTH;

  // 列幅をプロジェクトごとの localStorage キーへ保存する
  const persistColumnWidths = useCallback(
    (widths: Record<string, number>) => {
      // プロジェクト未選択時（全プロジェクト表示）はダッシュボードを開けないため保存しない
      if (!activeListId) return;
      try {
        localStorage.setItem(
          columnWidthsStorageKey(honbuId, roomId, activeListId),
          JSON.stringify(widths),
        );
      } catch {
        // プライベートモード等で localStorage が使えない場合は保存をあきらめる
      }
    },
    [honbuId, roomId, activeListId],
  );

  /**
   * ヘッダー境界のドラッグ開始。境界の左側の列（key）の幅を変更する。
   * 右へドラッグすれば広がり、左へドラッグすれば狭まる。
   */
  const handleColumnResizeStart = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>, key: string) => {
      e.preventDefault();
      // ヘッダーのソート用 onClick を発火させない
      e.stopPropagation();
      e.currentTarget.setPointerCapture(e.pointerId);
      const startX = e.clientX;
      const minWidth =
        key === TITLE_COLUMN_KEY ? TITLE_MIN_WIDTH : MIN_COLUMN_WIDTH;
      const startWidth =
        key === TITLE_COLUMN_KEY ? titleColumnWidth : getColumnWidth(key);

      // 他の列を広げたときにタイトル列（自動幅）が縮まないよう、
      // リサイズ開始時点の幅で固定する。ダブルクリックで自動幅に戻せる。
      // ドラッグ中の最新値をここで保持し、終了時にそのまま保存する。
      let latestWidths: Record<string, number> = columnWidths[TITLE_COLUMN_KEY]
        ? { ...columnWidths }
        : { ...columnWidths, [TITLE_COLUMN_KEY]: titleColumnWidth };
      setColumnWidths(latestWidths);

      const handleMove = (moveEvent: PointerEvent) => {
        const nextWidth = Math.max(
          minWidth,
          startWidth + moveEvent.clientX - startX,
        );
        latestWidths = { ...latestWidths, [key]: nextWidth };
        setColumnWidths(latestWidths);
      };
      const handleUp = () => {
        window.removeEventListener("pointermove", handleMove);
        window.removeEventListener("pointerup", handleUp);
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
        setResizingColumnKey(null);
        // ドラッグ完了時にのみ保存する（移動中の連続書き込みを避ける）
        persistColumnWidths(latestWidths);
      };
      document.body.style.userSelect = "none";
      document.body.style.cursor = "col-resize";
      setResizingColumnKey(key);
      window.addEventListener("pointermove", handleMove);
      window.addEventListener("pointerup", handleUp);
    },
    [columnWidths, titleColumnWidth, getColumnWidth, persistColumnWidths],
  );

  /** 境界線のダブルクリックで、その列の幅を既定値に戻す */
  const handleColumnResizeReset = useCallback(
    (key: string) => {
      const next = { ...columnWidths };
      delete next[key];
      setColumnWidths(next);
      persistColumnWidths(next);
    },
    [columnWidths, persistColumnWidths],
  );

  // --- CSVテンプレートDL ---
  const handleDownloadTemplate = useCallback(async () => {
    if (!activeListId) return;
    const url = `/api/todolists/${activeListId}/csv-template?honbuId=${honbuId}&roomId=${roomId}&splitPeriod=${splitPeriod}`;
    const a = document.createElement("a");
    a.href = url;
    a.download = `task_template_${activeListId}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [activeListId, honbuId, roomId, splitPeriod]);

  // --- CSVインポート ---
  const handleImportFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      let file = e.target.files?.[0];
      if (!file || !activeListId) return;
      e.target.value = "";
      setImportLoading(true);
      setImportResult(null);
      try {
        // Excel（.xlsx / .xls）はブラウザ内で CSV に変換してから送信する
        if (isExcelFile(file.name)) {
          try {
            file = await convertExcelToCsvFile(file);
          } catch {
            setImportResult({
              successCount: 0,
              errorCount: 1,
              warningCount: 0,
              errors: [
                {
                  row: 0,
                  message:
                    "Excelファイルの読み込みに失敗しました。ファイルが破損していないか確認してください。",
                },
              ],
            });
            return;
          }
        }
        const form = new FormData();
        form.append("file", file);
        const res = await fetch(
          `/api/todolists/${activeListId}/import-csv?honbuId=${honbuId}&roomId=${roomId}`,
          { method: "POST", body: form },
        );
        if (!res.ok) {
          const text = await res.text();
          setImportResult({
            successCount: 0,
            errorCount: 1,
            warningCount: 0,
            errors: [{ row: 0, message: text }],
          });
          return;
        }
        const data = await res.json();
        setImportResult(data);
        if (data.successCount > 0) {
          refreshTodos();
          // 取り込んだタスクのカスタム項目値を再取得して画面に反映する
          reloadCustomColumnValues();
        }
      } catch {
        setImportResult({
          successCount: 0,
          errorCount: 1,
          warningCount: 0,
          errors: [{ row: 0, message: "通信エラーが発生しました。" }],
        });
      } finally {
        setImportLoading(false);
        // 取り込みが終わったらインポートダイアログを閉じ、結果ダイアログに切り替える
        setShowCsvImportDialog(false);
      }
    },
    [activeListId, honbuId, roomId, refreshTodos, reloadCustomColumnValues],
  );

  // スコアモード保存
  const handleScoreModeChange = useCallback(
    (mode: ScoreMode) => {
      setScoreMode(mode);
      setSelectedCard(null);
      if (activeListId) {
        localStorage.setItem(
          `dashboardScoreMode_${honbuId}_${roomId}_${activeListId}`,
          mode,
        );
      }
    },
    [activeListId, honbuId, roomId],
  );

  // --- フラット化タスク（削除済みを除く）---
  const allTasks = useMemo(
    () => flattenTasks(tasks).filter((t) => (t.status as string) !== "Deleted"),
    [tasks],
  );

  // サブタスク（子タスク）のIDセット
  const childTaskIds = useMemo(() => {
    const ids = new Set<string>();
    const collect = (list: Task[]) => {
      for (const t of list) {
        if (t.childTasks) {
          for (const c of t.childTasks) ids.add(c.id);
          collect(t.childTasks);
        }
      }
    };
    collect(tasks);
    return ids;
  }, [tasks]);

  // サブタスクID -> 親タスク のマップ（並び順固定・親タスク名表示に使用）
  const parentMap = useMemo(() => {
    const map = new Map<string, Task>();
    const collect = (list: Task[]) => {
      for (const t of list) {
        if (t.childTasks) {
          for (const c of t.childTasks) map.set(c.id, t);
          collect(t.childTasks);
        }
      }
    };
    collect(tasks);
    return map;
  }, [tasks]);

  // --- スコア集計 ---
  const deadlineCounts = useMemo(() => {
    const counts: Record<string, number> = { overdue: 0 };
    for (const card of effectiveScorecardSettings.deadlineCards) {
      counts[deadlineCardKey(card)] = 0;
    }
    for (const t of allTasks) {
      const keys = getDeadlineKeys(
        t,
        jstToday,
        effectiveScorecardSettings.deadlineCards,
      );
      for (const key of keys) {
        counts[key] = (counts[key] ?? 0) + 1;
      }
    }
    return counts;
  }, [allTasks, jstToday, effectiveScorecardSettings.deadlineCards]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const t of allTasks) {
      const s = t.status || "未対応";
      counts[s] = (counts[s] ?? 0) + 1;
    }
    return counts;
  }, [allTasks]);

  const priorityCounts = useMemo(() => {
    const counts: Record<string, number> = { 高: 0, 中: 0, 低: 0, なし: 0 };
    for (const t of allTasks) {
      const p = t.priority || "なし";
      counts[p] = (counts[p] ?? 0) + 1;
    }
    return counts;
  }, [allTasks]);

  // タグカード: {tagId|"__none__": count}、タグ名も保持
  const tagCardData = useMemo(() => {
    const counts: Record<
      string,
      { label: string; color: string; count: number }
    > = {};
    for (const t of allTasks) {
      if (!t.tags || t.tags.length === 0) {
        counts["__none__"] = {
          label: "タグなし",
          color: "#9E9E9E",
          count: (counts["__none__"]?.count ?? 0) + 1,
        };
      } else {
        for (const tag of t.tags) {
          const key = String(tag.id);
          counts[key] = {
            label: tag.name,
            color: tag.color || "#9E9E9E",
            count: (counts[key]?.count ?? 0) + 1,
          };
        }
      }
    }
    return counts;
  }, [allTasks]);

  // タグカードをタスク数降順でソート（タグなしは末尾固定）
  const tagCardsSorted = useMemo(() => {
    const entries = Object.entries(tagCardData).filter(
      ([k]) => k !== "__none__",
    );
    entries.sort((a, b) => b[1].count - a[1].count);
    const none = tagCardData["__none__"];
    return { sorted: entries, none };
  }, [tagCardData]);

  // 担当者カード
  const assigneeCardData = useMemo(() => {
    const counts: Record<string, { label: string; count: number }> = {};
    for (const t of allTasks) {
      if (!t.assignees || t.assignees.length === 0) {
        counts["__none__"] = {
          label: "担当者なし",
          count: (counts["__none__"]?.count ?? 0) + 1,
        };
      } else {
        for (const u of t.assignees) {
          counts[u.id] = {
            label: u.name,
            count: (counts[u.id]?.count ?? 0) + 1,
          };
        }
      }
    }
    return counts;
  }, [allTasks]);

  // 担当者カード: 自分先頭 → 残りタスク数降順（担当者なし末尾固定）
  const assigneeCardsSorted = useMemo(() => {
    const selfId = String(honbuId);
    const entries = Object.entries(assigneeCardData).filter(
      ([k]) => k !== "__none__",
    );
    const self = entries.find(([k]) => k === selfId);
    const others = entries
      .filter(([k]) => k !== selfId)
      .sort((a, b) => b[1].count - a[1].count);
    const sorted = self ? [self, ...others] : others;
    const none = assigneeCardData["__none__"];
    return { sorted, none };
  }, [assigneeCardData, honbuId]);

  // カスタム列（select型）カード
  const customCardData = useMemo(() => {
    const result: Record<number, Record<string, number>> = {};
    const selectCols = customColumns.filter((c) => c.columnType === "select");
    for (const col of selectCols) {
      const counts: Record<string, number> = {};
      for (const t of allTasks) {
        const val =
          customColumnValues.find(
            (v) =>
              v.taskId === parseInt(t.id) &&
              v.customColumnId === col.customColumnId,
          )?.value ?? "";
        const key = val || "__none__";
        counts[key] = (counts[key] ?? 0) + 1;
      }
      result[col.customColumnId] = counts;
    }
    return result;
  }, [allTasks, customColumns, customColumnValues]);

  // --- カード選択でフィルタ ---
  const filteredTasks = useMemo(() => {
    if (!selectedCard) return allTasks;
    if (scoreMode === "deadline") {
      return allTasks.filter((t) =>
        getDeadlineKeys(
          t,
          jstToday,
          effectiveScorecardSettings.deadlineCards,
        ).includes(selectedCard),
      );
    }
    if (scoreMode === "status") {
      return allTasks.filter((t) => (t.status || "未対応") === selectedCard);
    }
    if (scoreMode === "priority") {
      return allTasks.filter((t) => (t.priority || "なし") === selectedCard);
    }
    if (scoreMode === "tags") {
      if (selectedCard === "__none__")
        return allTasks.filter((t) => !t.tags || t.tags.length === 0);
      return allTasks.filter((t) =>
        t.tags?.some((tag) => String(tag.id) === selectedCard),
      );
    }
    if (scoreMode === "assignee") {
      if (selectedCard === "__none__")
        return allTasks.filter((t) => !t.assignees || t.assignees.length === 0);
      return allTasks.filter((t) =>
        t.assignees?.some((u) => u.id === selectedCard),
      );
    }
    if (scoreMode.startsWith("custom_")) {
      const colId = parseInt(scoreMode.replace("custom_", ""));
      return allTasks.filter((t) => {
        const val =
          customColumnValues.find(
            (v) => v.taskId === parseInt(t.id) && v.customColumnId === colId,
          )?.value ?? "";
        return (val || "__none__") === selectedCard;
      });
    }
    return allTasks;
  }, [
    allTasks,
    selectedCard,
    scoreMode,
    jstToday,
    effectiveScorecardSettings.deadlineCards,
    customColumnValues,
  ]);

  // --- ソート ---
  const sortedTasks = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;

    const compare = (a: Task, b: Task): number => {
      if (sortKey === "title") {
        return dir * a.title.localeCompare(b.title, "ja");
      }
      if (sortKey === "status") {
        return dir * (a.status ?? "").localeCompare(b.status ?? "", "ja");
      }
      if (sortKey === "endDate") {
        const ea = toDateStr(a.endDate);
        const eb = toDateStr(b.endDate);
        if (!ea && !eb) return 0;
        if (!ea) return 1;
        if (!eb) return -1;
        return dir * ea.localeCompare(eb);
      }
      if (sortKey === "priority") {
        const pa = PRIORITY_ORDER[a.priority] ?? 3;
        const pb = PRIORITY_ORDER[b.priority] ?? 3;
        return dir * (pa - pb);
      }
      // カスタム列ソート
      const colId = parseInt(sortKey.replace("col_", ""));
      const va =
        customColumnValues.find(
          (v) => v.taskId === parseInt(a.id) && v.customColumnId === colId,
        )?.value ?? "";
      const vb =
        customColumnValues.find(
          (v) => v.taskId === parseInt(b.id) && v.customColumnId === colId,
        )?.value ?? "";
      return dir * (va ?? "").localeCompare(vb ?? "", "ja");
    };

    // メインタスクのみをソート対象にし、サブタスクは並び順を固定して
    // 親タスクの直下に元のツリー順で配置する。
    // 絞り込みで親が結果に含まれないサブタスク（orphan）は、単独ブロックとして
    // メインタスクと同じ基準でソート位置を決める。
    const inFilter = new Set(filteredTasks.map((t) => t.id));
    const subsByParent = new Map<string, Task[]>();
    const orphanSubs: Task[] = [];
    for (const t of filteredTasks) {
      if (!childTaskIds.has(t.id)) continue;
      const parent = parentMap.get(t.id);
      if (parent && inFilter.has(parent.id)) {
        const arr = subsByParent.get(parent.id) ?? [];
        arr.push(t); // filteredTasks はツリー順なのでサブタスクの順序は維持される
        subsByParent.set(parent.id, arr);
      } else {
        orphanSubs.push(t);
      }
    }

    type Block = { rep: Task; rows: Task[] };
    const blocks: Block[] = [
      ...filteredTasks
        .filter((t) => !childTaskIds.has(t.id))
        .map((m) => ({
          rep: m,
          rows: [m, ...(subsByParent.get(m.id) ?? [])],
        })),
      ...orphanSubs.map((s) => ({ rep: s, rows: [s] })),
    ];
    blocks.sort((x, y) => compare(x.rep, y.rep));
    return blocks.flatMap((b) => b.rows);
  }, [
    filteredTasks,
    sortKey,
    sortDir,
    customColumnValues,
    childTaskIds,
    parentMap,
  ]);

  const handleSortClick = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  // --- カード操作 ---
  const handleCardClick = (key: string) => {
    setSelectedCard((prev) => (prev === key ? null : key));
  };

  // --- ステータス変更 ---
  const handleStatusChange = async (task: Task, newStatus: Task["status"]) => {
    setEditingStatusTaskId(null);
    await updateTaskStatus(task.id, newStatus);
  };

  // --- 標準フィールド更新DTO構築 ---
  const buildUpdateDto = useCallback(
    (task: Task, updates: Partial<TaskUpdateDto>): TaskUpdateDto => ({
      title: task.title,
      tagIds: (task.tags || []).map((t) => t.id),
      startDate: task.startDate,
      endDate: task.endDate,
      memo: task.memo,
      notification: task.notification,
      priority: task.priority,
      type: task.type,
      assigneeHonbuIds: (task.assignees || []).map((u) => parseInt(u.id, 10)),
      requesterHonbuIds: (task.requesters || []).map((u) => parseInt(u.id, 10)),
      ...updates,
    }),
    [],
  );

  // --- タイトル確定 ---
  const handleTitleCommit = useCallback(
    (task: Task, draft: string) => {
      setFieldEdit(null);
      const trimmed = draft.trim();
      if (trimmed && trimmed !== task.title) {
        updateTask(task.id, buildUpdateDto(task, { title: trimmed }));
      }
    },
    [updateTask, buildUpdateDto],
  );

  // --- 担当者保存 ---
  const handleAssigneeSave = useCallback(
    (selectedNames: string[]) => {
      if (!memberDialogTask) return;
      const ids = selectedNames
        .map((name) => roomMembers.find((m) => m.name === name)?.id)
        .filter((id): id is string => !!id)
        .map((id) => parseInt(id, 10));
      updateTask(
        memberDialogTask.id,
        buildUpdateDto(memberDialogTask, { assigneeHonbuIds: ids }),
      );
      setMemberDialogTask(null);
    },
    [memberDialogTask, roomMembers, updateTask, buildUpdateDto],
  );

  // --- カスタム列値取得 ---
  const getCellValue = (taskId: string, columnId: number): string => {
    return (
      customColumnValues.find(
        (v) => v.taskId === parseInt(taskId) && v.customColumnId === columnId,
      )?.value ?? ""
    );
  };

  // --- カスタム列値保存 ---
  const saveCellValue = useCallback(
    async (taskId: string, columnId: number, value: string) => {
      const key = `${taskId}_${columnId}`;
      setLoadingCellKey(key);
      const prev = customColumnValues.find(
        (v) => v.taskId === parseInt(taskId) && v.customColumnId === columnId,
      );
      // 楽観的更新
      setCustomColumnValues((vals) => {
        const without = vals.filter(
          (v) =>
            !(v.taskId === parseInt(taskId) && v.customColumnId === columnId),
        );
        return [
          ...without,
          { taskId: parseInt(taskId), customColumnId: columnId, value },
        ];
      });
      try {
        const res = await fetch(
          `/api/tasks/${taskId}/custom-column-values/${columnId}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ value }),
          },
        );
        if (!res.ok) throw new Error();
      } catch {
        // ロールバック
        setCustomColumnValues((vals) => {
          const without = vals.filter(
            (v) =>
              !(v.taskId === parseInt(taskId) && v.customColumnId === columnId),
          );
          if (prev) return [...without, prev];
          return without;
        });
      } finally {
        setLoadingCellKey(null);
      }
    },
    [customColumnValues],
  );

  const checkDropdownDirection = useCallback((e: React.MouseEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    // スクロールコンテナの下端には sticky な「新規作成」行が常駐するため、
    // ビューポート下端ではなくコンテナ下端から sticky 行の高さを除いた位置を基準に判定する。
    // （テーブルがビューポートを満たさない場合でも、ピッカーが白い新規作成行の下に潜るのを防ぐ）
    const STICKY_NEW_ROW_HEIGHT = 44;
    const containerBottom =
      tableScrollRef.current?.getBoundingClientRect().bottom ??
      window.innerHeight;
    const spaceBelow = containerBottom - STICKY_NEW_ROW_HEIGHT - rect.bottom;
    setDropdownOpenUpward(spaceBelow < 220);
  }, []);

  // セル編集開始
  const startCellEdit = (taskId: string, columnId: number) => {
    const val = getCellValue(taskId, columnId);
    setEditingCell({ taskId, columnId });
    setCellDraft(val);
    setTimeout(() => cellInputRef.current?.focus(), 50);
  };

  // セル編集確定
  const commitCellEdit = () => {
    if (!editingCell) return;
    const current = getCellValue(editingCell.taskId, editingCell.columnId);
    if (cellDraft !== current) {
      saveCellValue(editingCell.taskId, editingCell.columnId, cellDraft);
    }
    setEditingCell(null);
  };

  // --- 三点メニューのクリック外クローズ ---
  useEffect(() => {
    if (!openMenuTaskId) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        menuPopoverRef.current &&
        !menuPopoverRef.current.contains(e.target as Node)
      ) {
        setOpenMenuTaskId(null);
        setShowSubtaskTemplatesForTaskId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openMenuTaskId]);

  // --- テンプレート取得（サブタスク用） ---
  useEffect(() => {
    if (!showSubtaskTemplatesForTaskId) return;
    const fetch_ = async () => {
      try {
        const res = await fetch(
          `/api/templates?roomId=${roomId}&honbuId=${honbuId}`,
        );
        if (res.ok) setSubtaskTemplates(await res.json());
      } catch {
        /* ignore */
      }
    };
    fetch_();
  }, [showSubtaskTemplatesForTaskId, roomId, honbuId]);

  // --- サブタスク追加確定 ---
  const handleAddSubtaskCommit = useCallback(
    async (parentTaskId: string) => {
      const title = newSubtaskTitle.trim();
      if (!title || !addSubtask) return;
      await addSubtask(parentTaskId, title);
      setAddingSubtaskForTaskId(null);
      setNewSubtaskTitle("");
      refreshTodos();
    },
    [newSubtaskTitle, addSubtask, refreshTodos],
  );

  // --- テンプレートからサブタスク追加 ---
  const handleApplySubtaskTemplate = useCallback(
    async (parentTaskId: string, template: TaskTemplate) => {
      const taskData = JSON.parse(template.taskData || "{}");
      const childTasks = taskData.childTasks || [];
      const tasksToCreate = childTasks.length > 0 ? childTasks : [taskData];
      try {
        for (const subtask of tasksToCreate) {
          await fetch(`/api/tasks/${parentTaskId}/subtasks`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              title: subtask.title || "テンプレートから作成",
            }),
          });
        }
        refreshTodos();
      } catch {
        /* ignore */
      }
      setShowSubtaskTemplatesForTaskId(null);
      setOpenMenuTaskId(null);
    },
    [refreshTodos],
  );

  // --- ステータス表示 ---
  const allStatuses = useMemo(() => {
    const base = ["未対応", "対応中", "待機", "完了", "非表示"];
    // roomStatuses はユーザー設定順（API が order でソート済み）。
    // 設定順を優先し、未登録の基本ステータスを後ろに補完する。
    const ordered = roomStatuses.map((s) => s.statusName);
    return [...new Set([...ordered, ...base])];
  }, [roomStatuses]);

  const getStatusColor = (status: string): string => {
    const rs = roomStatuses.find((s) => s.statusName === status);
    if (rs) return PaletteColorHex[rs.color] ?? "#9E9E9E";
    const map: Record<string, string> = {
      未対応: "#9E9E9E",
      対応中: "#3989FA",
      待機: "#FCA733",
      完了: "#40C063",
      非表示: "#BDBDBD",
    };
    return map[status] ?? "#9E9E9E";
  };

  // --- スタイル ---
  // ダークモードでは線・文字が背景に沈みやすいため、ボーダーは明るめ＋太め、
  // テキストは白寄りにして視認性を確保する
  const bg = isDarkMode ? "#121212" : "#f5f5f5";
  const cardBg = isDarkMode ? "#1e1e1e" : "#ffffff";
  const borderColor = isDarkMode ? "#5a5a5a" : "#e5e7eb";
  const textPrimary = isDarkMode ? "#f5f5f5" : "#212121";
  const textSecondary = isDarkMode ? "#c0c0c0" : "#757575";
  const headerBg = isDarkMode ? "#1a1a1a" : "#f9fafb";
  const borderW = isDarkMode ? "1.5px" : "1px";

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col)
      return (
        <span className="opacity-20 ml-1">
          <ArrowUpwardIcon style={{ fontSize: 12 }} />
        </span>
      );
    return sortDir === "asc" ? (
      <ArrowUpwardIcon style={{ fontSize: 12, marginLeft: 2 }} />
    ) : (
      <ArrowDownwardIcon style={{ fontSize: 12, marginLeft: 2 }} />
    );
  };

  const thClass = `relative px-3 py-2 text-xs font-semibold text-left cursor-pointer select-none whitespace-nowrap`;

  // タイトル以外の各ヘッダー列に適用する共通スタイル（幅は <colgroup> 側で指定）
  const columnThStyle = (): React.CSSProperties => ({
    color: textSecondary,
  });

  /**
   * ヘッダー列の右端に置く境界線（＝リサイズハンドル）。
   * 見た目は 1px の線だが、掴みやすさのため当たり判定を左右に広げている。
   * ドラッグで key の列（境界の左側）の幅が変わる。
   */
  const renderColumnResizeHandle = (key: string) => (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="列幅を変更"
      title="ドラッグで列幅を変更（ダブルクリックで既定幅に戻す）"
      onPointerDown={(e) => handleColumnResizeStart(e, key)}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => {
        e.stopPropagation();
        handleColumnResizeReset(key);
      }}
      className={`dash-col-resize-handle absolute top-0 h-full flex items-center justify-center ${
        resizingColumnKey === key ? "is-resizing" : ""
      }`}
      style={
        {
          right: -4,
          width: 8,
          zIndex: 2,
          "--dash-col-line": borderColor,
        } as React.CSSProperties
      }
    >
      <div className="dash-col-resize-line" />
    </div>
  );

  // 詳細パネルの幅（オーバーレイ・ピン留め固定表示で共通）。
  // 左辺のハンドルをドラッグして一時的に変更できる（リロードで既定に戻る）。
  const {
    width: detailPanelWidth,
    isResizing: isDetailResizing,
    handleResizeStart: handleDetailResizeStart,
  } = useResizablePanelWidth();
  // ピン留めが有効で表示対象がある状態か
  const showPinnedPanel = isDetailPinned && !!detailTask;

  // 詳細パネル本体（オーバーレイ表示・ピン留め固定表示で共用）。
  // displayMode をピン留め状態で切り替え、ピン留めトグルを常に渡す。
  const detailPanel = detailTask ? (
    <TaskDetailPanel
      task={detailTask}
      isSubtask={childTaskIds.has(detailTask.id)}
      parentForContext={
        childTaskIds.has(detailTask.id)
          ? (tasks.find((t) =>
              t.childTasks?.some((c) => c.id === detailTask.id),
            ) ?? detailTask)
          : detailTask
      }
      onUpdateTask={updateTask}
      onUpdateStatus={updateTaskStatus}
      onDeleteTask={onDeleteTask}
      roomMembers={roomMembers}
      availableTags={availableTags}
      onManageTags={onManageTags ?? (() => {})}
      color={color}
      addReminder={addReminder ?? (async () => null)}
      deleteReminder={deleteReminder ?? (async () => false)}
      onClose={() => setDetailTask(null)}
      displayMode={isDetailPinned ? "panel" : "overlay"}
      roomStatuses={roomStatuses}
      forceReload={forceReload ?? refreshTodos}
      honbuId={honbuId}
      roomId={roomId}
      listId={activeListId ? parseInt(activeListId, 10) : undefined}
      initialCustomColumnValues={customColumnValues.filter(
        (v) => v.taskId === parseInt(detailTask.id, 10),
      )}
      onOpenRecurrenceManagement={onOpenRecurrenceManagement}
      showPinToggle
      isPinned={isDetailPinned}
      onTogglePin={handleToggleDetailPin}
    />
  ) : null;

  return (
    <div
      className="flex flex-col h-full overflow-hidden"
      style={{
        background: bg,
        color: textPrimary,
        // ピン留め時は右端に詳細パネル分の余白を確保し、内容が隠れないようにする
        paddingRight: showPinnedPanel ? detailPanelWidth : 0,
        // ドラッグ中はトランジションを切り、パネルと余白の追従を一致させる
        transition: isDetailResizing ? "none" : "padding-right 200ms ease-out",
      }}
    >
      {/* ========== スコアエリア ========== */}
      <div
        className="flex-shrink-0 px-4 pt-4 pb-3"
        style={{ borderBottom: `${borderW} solid ${borderColor}` }}
      >
        {/* 行1: タイトル + フィルター解除 */}
        <div className="flex items-center gap-2 mb-2">
          <DashboardOutlinedIcon style={{ fontSize: 18, color: accentColor }} />
          <span
            className="text-sm font-semibold"
            style={{ color: accentColor }}
          >
            ダッシュボード
          </span>
        </div>

        {/* 行2: スコアモード タブ（横スクロール）+ 編集ボタン */}
        <div className="relative flex items-center mb-3 gap-1">
          {/* スクロール領域 */}
          <div className="flex-1 overflow-x-auto">
            <div
              className="flex rounded-lg overflow-hidden border text-xs"
              style={{ borderColor, borderWidth: borderW, width: "max-content" }}
            >
              {effectiveScorecardSettings.visibleModes
                // MyTODO（roomId=0）は担当者の概念がないため担当者タブを出さない
                .filter(
                  (m) => m.isVisible && (m.mode !== "assignee" || roomId !== 0),
                )
                // 非表示は表示順に関わらず常に右端へ
                .sort((a, b) => {
                  if (a.mode === "hidden") return 1;
                  if (b.mode === "hidden") return -1;
                  return a.displayOrder - b.displayOrder;
                })
                .map((m) => {
                  // 非表示タブ以外（期限・ステータス含む）はすべて削除可能
                  const removable = isScorecardEditMode && m.mode !== "hidden";
                  const isActive = scoreMode === m.mode;
                  return (
                    <Fragment key={m.mode}>
                      {/* 非表示の左に「カテゴリを追加」 */}
                      {isScorecardEditMode && m.mode === "hidden" && (
                        <button
                          onClick={() => setShowAddCategoryMenu((v) => !v)}
                          disabled={addableModes.length === 0}
                          className="flex items-center gap-0.5 px-3 py-1 whitespace-nowrap transition-colors"
                          style={{
                            background: cardBg,
                            color:
                              addableModes.length === 0
                                ? textSecondary
                                : accentColor,
                            borderRight: `${borderW} solid ${borderColor}`,
                            opacity: addableModes.length === 0 ? 0.5 : 1,
                          }}
                        >
                          <AddIcon style={{ fontSize: 13 }} />
                          カテゴリを追加
                        </button>
                      )}
                      <div
                        className="flex items-center whitespace-nowrap"
                        style={{
                          borderRight:
                            m.mode === "hidden"
                              ? undefined
                              : `${borderW} solid ${borderColor}`,
                        }}
                      >
                        <button
                          onClick={() => handleScoreModeChange(m.mode)}
                          className="px-3 py-1 transition-colors"
                          style={
                            isActive
                              ? { background: accentColor, color: "#fff" }
                              : { background: cardBg, color: textSecondary }
                          }
                        >
                          {m.label}
                        </button>
                        {removable && (
                          <button
                            onClick={() => handleRemoveCategory(m.mode)}
                            title="このカテゴリを削除"
                            className="flex items-center px-1.5 py-1 transition-colors hover:opacity-70"
                            style={{
                              background: isActive ? accentColor : cardBg,
                              color: isActive ? "#fff" : "#EF4444",
                            }}
                          >
                            <DeleteOutlineIcon style={{ fontSize: 14 }} />
                          </button>
                        )}
                      </div>
                    </Fragment>
                  );
                })}
            </div>
          </div>

          {/* 表示を編集 / 修正を保存（非表示の右） */}
          <button
            onClick={
              isScorecardEditMode ? handleSaveScorecard : enterScorecardEditMode
            }
            className="flex-shrink-0 flex items-center gap-1 px-3 py-1 rounded text-xs font-medium transition-colors hover:opacity-85"
            style={
              isScorecardEditMode
                ? { background: accentColor, color: "#fff" }
                : { border: `${borderW} solid ${borderColor}`, color: textSecondary }
            }
          >
            {isScorecardEditMode ? (
              <>
                <CheckIcon style={{ fontSize: 14 }} />
                修正を保存
              </>
            ) : (
              <>
                <EditOutlinedIcon style={{ fontSize: 14 }} />
                表示を編集
              </>
            )}
          </button>

          {/* スコアカード 共通/個人 切り替え */}
          <div
            className="flex-shrink-0 flex rounded overflow-hidden text-xs"
            style={{ border: `${borderW} solid ${borderColor}` }}
          >
            <button
              onClick={() => handleScorecardModeChange("common")}
              className="flex items-center gap-0.5 px-2 py-1 transition-colors"
              style={
                scorecardMode === "common"
                  ? { color: accentColor, background: accentColor + "18" }
                  : { color: textSecondary }
              }
            >
              <PeopleOutlineIcon style={{ fontSize: 12 }} />
              共通
            </button>
            <button
              onClick={() => handleScorecardModeChange("personal")}
              className="flex items-center gap-0.5 px-2 py-1 transition-colors"
              style={
                scorecardMode === "personal"
                  ? { color: accentColor, background: accentColor + "18" }
                  : { color: textSecondary }
              }
            >
              <PersonOutlineIcon style={{ fontSize: 12 }} />
              個人
            </button>
          </div>

          {/* カテゴリ追加メニュー（ドロップダウン） */}
          {isScorecardEditMode && showAddCategoryMenu && (
            <>
              <div
                className="fixed inset-0 z-20"
                onClick={() => setShowAddCategoryMenu(false)}
              />
              <div
                className="absolute z-30 top-full left-0 mt-1 rounded-lg shadow-xl overflow-hidden"
                style={{
                  background: cardBg,
                  border: `${borderW} solid ${borderColor}`,
                  minWidth: 180,
                }}
              >
                <div
                  className="px-3 py-2 text-xs font-semibold"
                  style={{
                    color: textSecondary,
                    borderBottom: `${borderW} solid ${borderColor}`,
                  }}
                >
                  追加するカテゴリ
                </div>
                {addableModes.length === 0 ? (
                  <div
                    className="px-3 py-3 text-xs"
                    style={{ color: textSecondary }}
                  >
                    追加できるカテゴリはありません
                  </div>
                ) : (
                  addableModes.map((def) => (
                    <button
                      key={def.mode}
                      onClick={() => {
                        handleAddCategory(def);
                        if (addableModes.length <= 1)
                          setShowAddCategoryMenu(false);
                      }}
                      className="flex items-center gap-2 w-full px-3 py-2 text-sm text-left transition-colors hover:bg-black/5"
                      style={{ color: textPrimary }}
                    >
                      <AddIcon style={{ fontSize: 15, color: accentColor }} />
                      {def.label}
                    </button>
                  ))
                )}
              </div>
            </>
          )}
        </div>

        {/* スコアカード */}
        {scoreMode !== "hidden" && (
          <div
            className={`flex gap-3 overflow-x-auto pb-1 items-stretch ${
              isScorecardEditMode && scoreMode === "deadline" ? "pt-3" : ""
            }`}
          >
            {/* 期限モード */}
            {scoreMode === "deadline" && (
              <>
                {/* 固定: 期限切れ */}
                {(() => {
                  const c = DEADLINE_FIXED.overdue;
                  const count = deadlineCounts["overdue"] ?? 0;
                  const isSelected = selectedCard === "overdue";
                  return (
                    <button
                      key="overdue"
                      onClick={() => handleCardClick("overdue")}
                      className="flex-shrink-0 rounded-xl px-4 py-3 text-center transition-all min-w-[100px]"
                      style={{
                        background: isSelected
                          ? isDarkMode
                            ? c.color + "33"
                            : c.bg
                          : cardBg,
                        border: `2px solid ${isSelected ? c.color : borderColor}`,
                        boxShadow: isSelected
                          ? `0 0 0 1px ${c.color}40`
                          : undefined,
                      }}
                    >
                      <div
                        className="text-xs font-medium mb-1"
                        style={{ color: c.color }}
                      >
                        {c.label}
                      </div>
                      <div
                        className="text-2xl font-bold"
                        style={{ color: isSelected ? c.color : textPrimary }}
                      >
                        {count}
                      </div>
                      <div
                        className="text-xs mt-0.5"
                        style={{ color: textSecondary }}
                      >
                        タスク
                      </div>
                    </button>
                  );
                })()}

                {/* 動的: 期限●～●日カード（登録順。編集モードでドラッグ&ドロップ並び替え可能） */}
                <DragDropContext onDragEnd={handleDeadlineCardDragEnd}>
                  <Droppable
                    droppableId="deadline-cards"
                    direction="horizontal"
                    isDropDisabled={!isScorecardEditMode}
                  >
                    {(dropProvided) => (
                      <div
                        ref={dropProvided.innerRef}
                        {...dropProvided.droppableProps}
                        className={`flex gap-3 items-stretch ${
                          effectiveScorecardSettings.deadlineCards.length === 0
                            ? "hidden"
                            : ""
                        }`}
                      >
                        {effectiveScorecardSettings.deadlineCards.map(
                          (card, origIndex) => {
                            const key = deadlineCardKey(card);
                            const count = deadlineCounts[key] ?? 0;
                            const isSelected = selectedCard === key;
                            const isEditingThisCard =
                              isScorecardEditMode &&
                              editingCardIdx === origIndex;
                            const cardColor = toPaletteColor(card.color);
                            const cardHex = PaletteColorHex[cardColor];
                            return (
                              <Draggable
                                key={`dl-card-${origIndex}`}
                                draggableId={`dl-card-${origIndex}`}
                                index={origIndex}
                                isDragDisabled={
                                  !isScorecardEditMode || isEditingThisCard
                                }
                                disableInteractiveElementBlocking
                              >
                                {(dragProvided) => {
                                  // 編集中のインラインフォーム
                                  if (isEditingThisCard) {
                                    return (
                                      <div
                                        ref={dragProvided.innerRef}
                                        {...dragProvided.draggableProps}
                                        {...dragProvided.dragHandleProps}
                                        className="flex-shrink-0 rounded-xl px-3 py-2.5 min-w-[210px] flex flex-col gap-1.5"
                                        style={{
                                          ...dragProvided.draggableProps.style,
                                          background: cardBg,
                                          border: `2px solid ${cardHex}`,
                                        }}
                                      >
                                        <div className="flex items-center gap-1.5">
                                          <div
                                            ref={cardColorPickerRef}
                                            className="relative flex-shrink-0"
                                          >
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setIsCardColorPickerOpen(
                                                  (prev) => !prev,
                                                );
                                              }}
                                              title="カードの色を変更"
                                              aria-label="カードの色を変更"
                                              className="block w-5 h-5 rounded-full shadow-md transition-transform hover:scale-110"
                                              style={{
                                                backgroundColor: cardHex,
                                              }}
                                            />
                                            {isCardColorPickerOpen && (
                                              <div
                                                className="absolute left-0 top-full mt-2 p-2 rounded-md shadow-lg z-20 grid grid-cols-5 gap-2 w-40"
                                                style={{
                                                  background: isDarkMode
                                                    ? "#2a2a2a"
                                                    : "#fff",
                                                }}
                                              >
                                                {PALETTE_COLOR_KEYS.map(
                                                  (colorOption) => (
                                                    <button
                                                      key={colorOption}
                                                      onClick={() =>
                                                        handleUpdateDeadlineCardColor(
                                                          origIndex,
                                                          colorOption,
                                                        )
                                                      }
                                                      className="w-6 h-6 rounded-full transition-transform hover:scale-110 shadow-md flex items-center justify-center"
                                                      style={{
                                                        backgroundColor:
                                                          PaletteColorHex[
                                                            colorOption
                                                          ],
                                                      }}
                                                      aria-label={colorOption}
                                                    >
                                                      {cardColor ===
                                                        colorOption && (
                                                        <CheckIcon
                                                          style={{
                                                            color: "white",
                                                            fontSize: "1rem",
                                                          }}
                                                        />
                                                      )}
                                                    </button>
                                                  ),
                                                )}
                                              </div>
                                            )}
                                          </div>
                                          <input
                                            type="text"
                                            value={card.label}
                                            onChange={(e) =>
                                              handleUpdateDeadlineCardLabel(
                                                origIndex,
                                                e.target.value,
                                              )
                                            }
                                            placeholder="表示名"
                                            className="flex-1 min-w-0 text-xs border rounded px-1.5 py-1 focus:outline-none"
                                            style={{
                                              borderColor,
                                              background: isDarkMode
                                                ? "#121212"
                                                : "#fff",
                                              color: textPrimary,
                                            }}
                                          />
                                        </div>
                                        <div className="flex items-center gap-1">
                                          <span
                                            className="text-xs"
                                            style={{ color: textSecondary }}
                                          >
                                            期限
                                          </span>
                                          <input
                                            type="number"
                                            min={0}
                                            max={365}
                                            value={card.minDays ?? ""}
                                            placeholder="0"
                                            onChange={(e) =>
                                              handleUpdateDeadlineCardRange(
                                                origIndex,
                                                "minDays",
                                                e.target.value,
                                              )
                                            }
                                            className="w-12 text-center rounded border px-1 py-1 text-xs focus:outline-none"
                                            style={{
                                              borderColor: cardHex,
                                              background: isDarkMode
                                                ? "#121212"
                                                : "#fff",
                                              color: textPrimary,
                                            }}
                                          />
                                          <span
                                            className="text-xs"
                                            style={{ color: textSecondary }}
                                          >
                                            ～
                                          </span>
                                          <input
                                            type="number"
                                            min={0}
                                            max={365}
                                            value={card.maxDays ?? ""}
                                            placeholder="∞"
                                            onChange={(e) =>
                                              handleUpdateDeadlineCardRange(
                                                origIndex,
                                                "maxDays",
                                                e.target.value,
                                              )
                                            }
                                            className="w-12 text-center rounded border px-1 py-1 text-xs focus:outline-none"
                                            style={{
                                              borderColor: cardHex,
                                              background: isDarkMode
                                                ? "#121212"
                                                : "#fff",
                                              color: textPrimary,
                                            }}
                                          />
                                          <span
                                            className="text-xs"
                                            style={{ color: textSecondary }}
                                          >
                                            日前
                                          </span>
                                          <button
                                            onClick={() =>
                                              setEditingCardIdx(null)
                                            }
                                            disabled={
                                              (card.minDays == null &&
                                                card.maxDays == null) ||
                                              (card.minDays != null &&
                                                card.maxDays != null &&
                                                card.minDays > card.maxDays)
                                            }
                                            title="完了"
                                            className="flex items-center justify-center rounded p-1 transition-colors hover:opacity-80 disabled:opacity-40"
                                            style={{
                                              background: cardHex,
                                              color: "#fff",
                                            }}
                                          >
                                            <CheckIcon
                                              style={{ fontSize: 15 }}
                                            />
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  }

                                  return (
                                    <div
                                      ref={dragProvided.innerRef}
                                      {...dragProvided.draggableProps}
                                      {...dragProvided.dragHandleProps}
                                      className="relative flex-shrink-0"
                                    >
                                      <button
                                        onClick={() => handleCardClick(key)}
                                        className="rounded-xl px-4 py-3 text-center transition-all min-w-[100px]"
                                        style={{
                                          background: isSelected
                                            ? isDarkMode
                                              ? cardHex + "33"
                                              : PaletteColorBg[cardColor]
                                            : cardBg,
                                          border: `2px solid ${isSelected ? cardHex : borderColor}`,
                                          boxShadow: isSelected
                                            ? `0 0 0 1px ${cardHex}40`
                                            : undefined,
                                        }}
                                      >
                                        <div
                                          className="text-xs font-medium mb-1"
                                          style={{
                                            color: cardHex,
                                          }}
                                        >
                                          {card.label}
                                        </div>
                                        <div
                                          className="text-2xl font-bold"
                                          style={{
                                            color: isSelected
                                              ? cardHex
                                              : textPrimary,
                                          }}
                                        >
                                          {count}
                                        </div>
                                        <div
                                          className="text-xs mt-0.5"
                                          style={{ color: textSecondary }}
                                        >
                                          タスク
                                        </div>
                                      </button>
                                      {isScorecardEditMode && (
                                        <div className="absolute -top-2 -right-2 flex gap-1">
                                          <button
                                            onClick={() =>
                                              setEditingCardIdx(origIndex)
                                            }
                                            title="カードを編集"
                                            className="flex items-center justify-center rounded-full shadow w-6 h-6 transition-colors hover:opacity-80"
                                            style={{
                                              background: accentColor,
                                              color: "#fff",
                                            }}
                                          >
                                            <EditOutlinedIcon
                                              style={{ fontSize: 13 }}
                                            />
                                          </button>
                                          <button
                                            onClick={() =>
                                              handleRemoveDeadlineCard(
                                                origIndex,
                                              )
                                            }
                                            title="カードを削除"
                                            className="flex items-center justify-center rounded-full shadow w-6 h-6 transition-colors hover:opacity-80"
                                            style={{
                                              background: "#EF4444",
                                              color: "#fff",
                                            }}
                                          >
                                            <DeleteOutlineIcon
                                              style={{ fontSize: 13 }}
                                            />
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  );
                                }}
                              </Draggable>
                            );
                          },
                        )}
                        {dropProvided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>

                {/* 編集モード: カードを追加（範囲カードの右） */}
                {isScorecardEditMode && (
                  <button
                    onClick={handleAddDeadlineCard}
                    className="flex-shrink-0 flex flex-col items-center justify-center rounded-xl px-4 py-3 transition-all min-w-[100px] hover:opacity-80"
                    style={{
                      border: `2px dashed ${DEADLINE_CUSTOM_COLOR}`,
                      color: DEADLINE_CUSTOM_COLOR,
                    }}
                  >
                    <AddIcon style={{ fontSize: 20 }} />
                    <span className="text-xs mt-0.5">カードを追加</span>
                  </button>
                )}
              </>
            )}

            {/* ステータスモード */}
            {scoreMode === "status" &&
              allStatuses
                .filter((s) => s !== "Deleted" && s !== "非表示")
                .map((status) => {
                  const count = statusCounts[status] ?? 0;
                  const sColor = getStatusColor(status);
                  const isSelected = selectedCard === status;
                  return (
                    <button
                      key={status}
                      onClick={() => handleCardClick(status)}
                      className="flex-shrink-0 rounded-xl px-4 py-3 text-center transition-all min-w-[100px]"
                      style={{
                        background: isSelected
                          ? isDarkMode
                            ? sColor + "22"
                            : sColor + "18"
                          : cardBg,
                        border: `2px solid ${isSelected ? sColor : borderColor}`,
                        boxShadow: isSelected
                          ? `0 0 0 1px ${sColor}40`
                          : undefined,
                      }}
                    >
                      <div
                        className="text-xs font-medium mb-1"
                        style={{ color: sColor }}
                      >
                        {status}
                      </div>
                      <div
                        className="text-2xl font-bold"
                        style={{ color: isSelected ? sColor : textPrimary }}
                      >
                        {count}
                      </div>
                      <div
                        className="text-xs mt-0.5"
                        style={{ color: textSecondary }}
                      >
                        タスク
                      </div>
                    </button>
                  );
                })}

            {/* 重要度モード */}
            {scoreMode === "priority" &&
              PRIORITY_CARDS.map((card) => {
                const count = priorityCounts[card.key] ?? 0;
                const isSelected = selectedCard === card.key;
                return (
                  <button
                    key={card.key}
                    onClick={() => handleCardClick(card.key)}
                    className="flex-shrink-0 rounded-xl px-4 py-3 text-center transition-all min-w-[100px]"
                    style={{
                      background: isSelected
                        ? isDarkMode
                          ? card.color + "33"
                          : card.bg
                        : cardBg,
                      border: `2px solid ${isSelected ? card.color : borderColor}`,
                      boxShadow: isSelected
                        ? `0 0 0 1px ${card.color}40`
                        : undefined,
                    }}
                  >
                    <div
                      className="text-xs font-medium mb-1"
                      style={{ color: card.color }}
                    >
                      {card.label}
                    </div>
                    <div
                      className="text-2xl font-bold"
                      style={{ color: isSelected ? card.color : textPrimary }}
                    >
                      {count}
                    </div>
                    <div
                      className="text-xs mt-0.5"
                      style={{ color: textSecondary }}
                    >
                      タスク
                    </div>
                  </button>
                );
              })}

            {/* タグモード */}
            {scoreMode === "tags" &&
              (() => {
                const limit = cardVisibleCount["tags"] ?? 10;
                const visible = tagCardsSorted.sorted.slice(0, limit);
                const hasMore = tagCardsSorted.sorted.length > limit;
                return (
                  <>
                    {visible.map(([key, data]) => {
                      const isSelected = selectedCard === key;
                      const dotColor = data.color.startsWith("#")
                        ? data.color
                        : "#9E9E9E";
                      return (
                        <button
                          key={key}
                          onClick={() => handleCardClick(key)}
                          className="flex-shrink-0 rounded-xl px-4 py-3 text-center transition-all min-w-[100px]"
                          style={{
                            background: isSelected
                              ? isDarkMode
                                ? dotColor + "33"
                                : dotColor + "18"
                              : cardBg,
                            border: `2px solid ${isSelected ? dotColor : borderColor}`,
                            boxShadow: isSelected
                              ? `0 0 0 1px ${dotColor}40`
                              : undefined,
                          }}
                        >
                          <div className="flex items-center justify-center gap-1 mb-1">
                            <span
                              className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                              style={{ background: dotColor }}
                            />
                            <span
                              className="text-xs font-medium truncate max-w-[80px]"
                              style={{ color: dotColor }}
                            >
                              {data.label}
                            </span>
                          </div>
                          <div
                            className="text-2xl font-bold"
                            style={{
                              color: isSelected ? dotColor : textPrimary,
                            }}
                          >
                            {data.count}
                          </div>
                          <div
                            className="text-xs mt-0.5"
                            style={{ color: textSecondary }}
                          >
                            タスク
                          </div>
                        </button>
                      );
                    })}
                    {/* +5件表示ボタン */}
                    {hasMore && (
                      <button
                        onClick={() =>
                          setCardVisibleCount((prev) => ({
                            ...prev,
                            tags: (prev["tags"] ?? 10) + 5,
                          }))
                        }
                        className="flex-shrink-0 rounded-xl px-3 py-3 text-center transition-all self-center"
                        style={{
                          border: `2px dashed ${borderColor}`,
                          color: textSecondary,
                          minWidth: 72,
                        }}
                      >
                        <div className="text-xs">+5件</div>
                        <div className="text-xs">表示</div>
                      </button>
                    )}
                    {/* タグなし（右端固定） */}
                    {tagCardsSorted.none &&
                      (() => {
                        const isSelected = selectedCard === "__none__";
                        return (
                          <button
                            onClick={() => handleCardClick("__none__")}
                            className="flex-shrink-0 rounded-xl px-4 py-3 text-center transition-all min-w-[100px]"
                            style={{
                              background: isSelected
                                ? isDarkMode
                                  ? "#9E9E9E33"
                                  : "#F3F4F6"
                                : cardBg,
                              border: `2px solid ${isSelected ? "#9E9E9E" : borderColor}`,
                            }}
                          >
                            <div
                              className="text-xs font-medium mb-1"
                              style={{ color: "#9E9E9E" }}
                            >
                              タグなし
                            </div>
                            <div
                              className="text-2xl font-bold"
                              style={{
                                color: isSelected ? "#9E9E9E" : textPrimary,
                              }}
                            >
                              {tagCardsSorted.none.count}
                            </div>
                            <div
                              className="text-xs mt-0.5"
                              style={{ color: textSecondary }}
                            >
                              タスク
                            </div>
                          </button>
                        );
                      })()}
                  </>
                );
              })()}

            {/* 担当者モード */}
            {scoreMode === "assignee" &&
              (() => {
                const limit = cardVisibleCount["assignee"] ?? 10;
                const visible = assigneeCardsSorted.sorted.slice(0, limit);
                const hasMore = assigneeCardsSorted.sorted.length > limit;
                return (
                  <>
                    {visible.map(([key, data]) => {
                      const isSelected = selectedCard === key;
                      const isSelf = key === String(honbuId);
                      const cardColor = isSelf ? accentColor : "#607D8B";
                      return (
                        <button
                          key={key}
                          onClick={() => handleCardClick(key)}
                          className="flex-shrink-0 rounded-xl px-4 py-3 text-center transition-all min-w-[100px]"
                          style={{
                            background: isSelected
                              ? isDarkMode
                                ? cardColor + "33"
                                : cardColor + "18"
                              : cardBg,
                            border: `2px solid ${isSelected ? cardColor : isSelf ? accentColor + "66" : borderColor}`,
                            boxShadow: isSelected
                              ? `0 0 0 1px ${cardColor}40`
                              : undefined,
                          }}
                        >
                          <div
                            className="text-xs font-medium mb-1 truncate max-w-[80px]"
                            style={{ color: cardColor }}
                          >
                            {isSelf ? "自分" : data.label}
                          </div>
                          <div
                            className="text-2xl font-bold"
                            style={{
                              color: isSelected ? cardColor : textPrimary,
                            }}
                          >
                            {data.count}
                          </div>
                          <div
                            className="text-xs mt-0.5"
                            style={{ color: textSecondary }}
                          >
                            タスク
                          </div>
                        </button>
                      );
                    })}
                    {/* +5件表示ボタン */}
                    {hasMore && (
                      <button
                        onClick={() =>
                          setCardVisibleCount((prev) => ({
                            ...prev,
                            assignee: (prev["assignee"] ?? 10) + 5,
                          }))
                        }
                        className="flex-shrink-0 rounded-xl px-3 py-3 text-center transition-all self-center"
                        style={{
                          border: `2px dashed ${borderColor}`,
                          color: textSecondary,
                          minWidth: 72,
                        }}
                      >
                        <div className="text-xs">+5件</div>
                        <div className="text-xs">表示</div>
                      </button>
                    )}
                    {/* 担当者なし（右端固定） */}
                    {assigneeCardsSorted.none &&
                      (() => {
                        const isSelected = selectedCard === "__none__";
                        return (
                          <button
                            onClick={() => handleCardClick("__none__")}
                            className="flex-shrink-0 rounded-xl px-4 py-3 text-center transition-all min-w-[100px]"
                            style={{
                              background: isSelected
                                ? isDarkMode
                                  ? "#9E9E9E33"
                                  : "#F3F4F6"
                                : cardBg,
                              border: `2px solid ${isSelected ? "#9E9E9E" : borderColor}`,
                            }}
                          >
                            <div
                              className="text-xs font-medium mb-1"
                              style={{ color: "#9E9E9E" }}
                            >
                              担当者なし
                            </div>
                            <div
                              className="text-2xl font-bold"
                              style={{
                                color: isSelected ? "#9E9E9E" : textPrimary,
                              }}
                            >
                              {assigneeCardsSorted.none.count}
                            </div>
                            <div
                              className="text-xs mt-0.5"
                              style={{ color: textSecondary }}
                            >
                              タスク
                            </div>
                          </button>
                        );
                      })()}
                  </>
                );
              })()}

            {/* カスタム列モード（select型） */}
            {scoreMode.startsWith("custom_") &&
              (() => {
                const colId = parseInt(scoreMode.replace("custom_", ""));
                const col = customColumns.find(
                  (c) =>
                    c.customColumnId === colId && c.columnType === "select",
                );
                if (!col) return null;
                const counts = customCardData[colId] ?? {};
                const modeKey = `custom_${colId}`;
                const limit = cardVisibleCount[modeKey] ?? 10;
                const optionCards = col.options
                  .sort((a, b) => a.displayOrder - b.displayOrder)
                  .map((opt) => ({
                    key: opt.optionValue,
                    label: opt.optionValue,
                    count: counts[opt.optionValue] ?? 0,
                  }));
                const visible = optionCards.slice(0, limit);
                const hasMore = optionCards.length > limit;
                const noneCount = counts["__none__"] ?? 0;
                return (
                  <>
                    {visible.map((item) => {
                      const isSelected = selectedCard === item.key;
                      return (
                        <button
                          key={item.key}
                          onClick={() => handleCardClick(item.key)}
                          className="flex-shrink-0 rounded-xl px-4 py-3 text-center transition-all min-w-[100px]"
                          style={{
                            background: isSelected
                              ? isDarkMode
                                ? accentColor + "33"
                                : accentColor + "18"
                              : cardBg,
                            border: `2px solid ${isSelected ? accentColor : borderColor}`,
                            boxShadow: isSelected
                              ? `0 0 0 1px ${accentColor}40`
                              : undefined,
                          }}
                        >
                          <div
                            className="text-xs font-medium mb-1 truncate max-w-[80px]"
                            style={{ color: accentColor }}
                          >
                            {item.label}
                          </div>
                          <div
                            className="text-2xl font-bold"
                            style={{
                              color: isSelected ? accentColor : textPrimary,
                            }}
                          >
                            {item.count}
                          </div>
                          <div
                            className="text-xs mt-0.5"
                            style={{ color: textSecondary }}
                          >
                            タスク
                          </div>
                        </button>
                      );
                    })}
                    {hasMore && (
                      <button
                        onClick={() =>
                          setCardVisibleCount((prev) => ({
                            ...prev,
                            [modeKey]: (prev[modeKey] ?? 10) + 5,
                          }))
                        }
                        className="flex-shrink-0 rounded-xl px-3 py-3 text-center transition-all self-center"
                        style={{
                          border: `2px dashed ${borderColor}`,
                          color: textSecondary,
                          minWidth: 72,
                        }}
                      >
                        <div className="text-xs">+5件</div>
                        <div className="text-xs">表示</div>
                      </button>
                    )}
                    {noneCount > 0 &&
                      (() => {
                        const isSelected = selectedCard === "__none__";
                        return (
                          <button
                            onClick={() => handleCardClick("__none__")}
                            className="flex-shrink-0 rounded-xl px-4 py-3 text-center transition-all min-w-[100px]"
                            style={{
                              background: isSelected
                                ? isDarkMode
                                  ? "#9E9E9E33"
                                  : "#F3F4F6"
                                : cardBg,
                              border: `2px solid ${isSelected ? "#9E9E9E" : borderColor}`,
                            }}
                          >
                            <div
                              className="text-xs font-medium mb-1"
                              style={{ color: "#9E9E9E" }}
                            >
                              未設定
                            </div>
                            <div
                              className="text-2xl font-bold"
                              style={{
                                color: isSelected ? "#9E9E9E" : textPrimary,
                              }}
                            >
                              {noneCount}
                            </div>
                            <div
                              className="text-xs mt-0.5"
                              style={{ color: textSecondary }}
                            >
                              タスク
                            </div>
                          </button>
                        );
                      })()}
                  </>
                );
              })()}
          </div>
        )}

        {/* 総タスク数（スコアカードの右下） */}
        {scoreMode !== "hidden" && (
          <div
            className="text-xs text-right pr-1 mt-1"
            style={{ color: textSecondary }}
          >
            全：{allTasks.length}タスク
          </div>
        )}
      </div>

      {/* ========== タスクテーブル ========== */}
      <div ref={tableScrollRef} className="flex-1 overflow-auto">
        <table
          className="dash-task-table border-collapse text-sm"
          style={{ width: tableTotalWidth, tableLayout: "fixed" }}
        >
          {/* 列幅は colgroup で制御（タイトルは未指定時のみ残り幅を吸収） */}
          <colgroup>
            <col style={{ width: titleColumnWidth }} />
            {activeColumnKeys.map((key) => (
              <col key={key} style={{ width: getColumnWidth(key) }} />
            ))}
            <col style={{ width: ADD_COLUMN_WIDTH }} />
          </colgroup>
          <thead
            className="sticky top-0 z-10"
            style={{
              background: headerBg,
              borderBottom: `${borderW} solid ${borderColor}`,
            }}
          >
            <tr>
              {/* タイトル */}
              <th
                className={thClass}
                style={{ color: textSecondary }}
                onClick={() => handleSortClick("title")}
              >
                タスクタイトル <SortIcon col="title" />
                {renderColumnResizeHandle(TITLE_COLUMN_KEY)}
              </th>
              {/* 動的列ヘッダー */}
              {activeColumnKeys.map((key) => {
                if (key === "status")
                  return (
                    <th
                      key="status"
                      className={thClass}
                      style={columnThStyle()}
                      onClick={() => handleSortClick("status")}
                    >
                      {FIXED_COLUMN_LABELS.status} <SortIcon col="status" />
                      {renderColumnResizeHandle(key)}
                    </th>
                  );
                if (key === "assignee")
                  return (
                    <th
                      key="assignee"
                      className={thClass}
                      style={columnThStyle()}
                    >
                      {FIXED_COLUMN_LABELS.assignee}
                      {renderColumnResizeHandle(key)}
                    </th>
                  );
                if (key === "endDate")
                  return (
                    <th
                      key="endDate"
                      className={thClass}
                      style={columnThStyle()}
                      onClick={() => handleSortClick("endDate")}
                    >
                      {FIXED_COLUMN_LABELS.endDate} <SortIcon col="endDate" />
                      {renderColumnResizeHandle(key)}
                    </th>
                  );
                if (key === "priority")
                  return (
                    <th
                      key="priority"
                      className={thClass}
                      style={columnThStyle()}
                      onClick={() => handleSortClick("priority")}
                    >
                      {FIXED_COLUMN_LABELS.priority} <SortIcon col="priority" />
                      {renderColumnResizeHandle(key)}
                    </th>
                  );
                if (key === "tags")
                  return (
                    <th key="tags" className={thClass} style={columnThStyle()}>
                      {FIXED_COLUMN_LABELS.tags}
                      {renderColumnResizeHandle(key)}
                    </th>
                  );
                if (key.startsWith("custom_")) {
                  const colId = parseInt(key.replace("custom_", ""));
                  const col = customColumns.find(
                    (c) => c.customColumnId === colId,
                  );
                  if (!col) return null;
                  return (
                    <th
                      key={key}
                      className={thClass}
                      style={columnThStyle()}
                      onClick={() => handleSortClick(`col_${colId}`)}
                    >
                      {col.columnName} <SortIcon col={`col_${colId}`} />
                      {renderColumnResizeHandle(key)}
                    </th>
                  );
                }
                return null;
              })}
              {/* ＋列追加（本文行の詳細アイコンと左端が揃う余白にする） */}
              <th className="pl-4 pr-1 py-2 text-left">
                <button
                  onClick={() => setShowColumnDialog(true)}
                  title="列を追加"
                  className="p-1 rounded hover:bg-gray-100 transition-colors"
                  style={{ color: accentColor }}
                >
                  <AddIcon style={{ fontSize: 16 }} />
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedTasks.length === 0 && (
              <tr>
                <td
                  colSpan={activeColumnKeys.length + 2}
                  className="text-center py-10 text-sm"
                  style={{ color: textSecondary }}
                >
                  {selectedCard
                    ? "該当タスクはありません"
                    : "タスクがありません"}
                </td>
              </tr>
            )}
            {sortedTasks.map((task) => {
              const isSubtask = childTaskIds.has(task.id);
              const startStr = toDateStr(task.startDate);
              const endStr = toDateStr(task.endDate);
              const days = endStr ? daysUntil(endStr, jstToday) : null;
              const isOverdue = days !== null && days < 0 && !task.completed;
              const isToday = days === 0;

              let dateColor = textSecondary;
              if (task.completed) dateColor = textSecondary;
              else if (isOverdue) dateColor = "#EF4444";
              else if (isToday) dateColor = "#F97316";

              return (
                <Fragment key={task.id}>
                  <tr
                    className="group border-b transition-colors hover:bg-black/5"
                    style={{
                      borderColor,
                      borderBottomWidth: borderW,
                      opacity: task.completed ? 0.5 : 1,
                    }}
                  >
                    {/* タイトル */}
                    <td className="px-3 py-2">
                      {/* 絞り込み中はサブタスクの上に親タスク名を表示する */}
                      {childTaskIds.has(task.id) &&
                        selectedCard &&
                        parentMap.get(task.id) && (
                          <div
                            className="text-xs mb-0.5 pl-[18px] truncate"
                            style={{ color: textSecondary }}
                            title={parentMap.get(task.id)!.title}
                          >
                            {parentMap.get(task.id)!.title}
                          </div>
                        )}
                      <div className="flex items-start gap-1">
                        {childTaskIds.has(task.id) && (
                          <SubdirectoryArrowRightIcon
                            style={{
                              fontSize: 14,
                              color: textSecondary,
                              marginTop: 2,
                              flexShrink: 0,
                            }}
                          />
                        )}
                        {fieldEdit?.taskId === task.id &&
                        fieldEdit.field === "title" ? (
                          <input
                            ref={titleInputRef}
                            className="w-full px-1 py-0.5 text-sm rounded border focus:outline-none"
                            style={{
                              borderColor: accentColor,
                              background: isDarkMode ? "#2a2a2a" : "#fff",
                              color: textPrimary,
                            }}
                            value={titleDraft}
                            autoFocus
                            onChange={(e) => setTitleDraft(e.target.value)}
                            onBlur={() => handleTitleCommit(task, titleDraft)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter")
                                handleTitleCommit(task, titleDraft);
                              if (e.key === "Escape") setFieldEdit(null);
                            }}
                          />
                        ) : (
                          <span
                            className={`text-sm leading-snug cursor-pointer hover:underline ${task.completed ? "line-through" : ""}`}
                            style={{
                              color: textPrimary,
                              wordBreak: "break-word",
                            }}
                            onClick={() => {
                              setTitleDraft(task.title);
                              setFieldEdit({ taskId: task.id, field: "title" });
                            }}
                          >
                            {task.title}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* 動的列セル */}
                    {activeColumnKeys.map((key) => {
                      if (key === "status")
                        return (
                          <td key="status" className="px-3 py-2 relative">
                            <div
                              ref={
                                editingStatusTaskId === task.id
                                  ? statusDropdownRef
                                  : undefined
                              }
                            >
                              <button
                                onClick={(e) => {
                                  checkDropdownDirection(e);
                                  setEditingStatusTaskId((prev) =>
                                    prev === task.id ? null : task.id,
                                  );
                                }}
                                className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs whitespace-nowrap"
                                style={{
                                  background:
                                    getStatusColor(task.status || "未対応") +
                                    "20",
                                  color: getStatusColor(
                                    task.status || "未対応",
                                  ),
                                  border: `1px solid ${getStatusColor(task.status || "未対応")}60`,
                                }}
                              >
                                {task.status || "未対応"}
                                <ExpandMoreIcon style={{ fontSize: 12 }} />
                              </button>
                              {editingStatusTaskId === task.id && (
                                <div
                                  className={`absolute left-2 z-40 rounded-lg shadow-lg border overflow-hidden ${dropdownOpenUpward ? "bottom-full mb-1" : "top-8"}`}
                                  style={{
                                    background: cardBg,
                                    borderColor,
                                    minWidth: 110,
                                  }}
                                >
                                  {allStatuses.map((s) => (
                                    <button
                                      key={s}
                                      onClick={() =>
                                        handleStatusChange(
                                          task,
                                          s as Task["status"],
                                        )
                                      }
                                      className="block w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 transition-colors"
                                      style={{
                                        color: getStatusColor(s),
                                        background:
                                          task.status === s
                                            ? getStatusColor(s) + "18"
                                            : undefined,
                                      }}
                                    >
                                      {s}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                        );
                      if (key === "assignee")
                        return (
                          <td
                            key="assignee"
                            className="px-3 py-2 cursor-pointer hover:bg-black/5 transition-colors"
                            onClick={() => setMemberDialogTask(task)}
                          >
                            <span
                              className="text-xs"
                              style={{
                                color: task.assignees.length
                                  ? textPrimary
                                  : textSecondary,
                              }}
                            >
                              {task.assignees.length
                                ? task.assignees.map((a) => a.name).join(", ")
                                : "—"}
                            </span>
                          </td>
                        );
                      if (key === "endDate") {
                        const hasRange =
                          !!startStr && !!endStr && startStr !== endStr;
                        const singleStr = startStr || endStr;
                        return (
                          <td
                            key="endDate"
                            className="px-3 py-2 relative cursor-pointer hover:bg-black/5 transition-colors"
                            onClick={(e) => {
                              checkDropdownDirection(e);
                              setFieldEdit((prev) =>
                                prev?.taskId === task.id &&
                                prev.field === "endDate"
                                  ? null
                                  : { taskId: task.id, field: "endDate" },
                              );
                            }}
                          >
                            <span className="text-xs text-left">
                              {hasRange ? (
                                <span
                                  className="flex flex-col leading-tight"
                                  style={{ color: dateColor }}
                                >
                                  <span>開始: {startStr}</span>
                                  <span>終了: {endStr}</span>
                                </span>
                              ) : (
                                <span
                                  style={{
                                    color: singleStr
                                      ? dateColor
                                      : textSecondary,
                                  }}
                                >
                                  {singleStr || "—"}
                                </span>
                              )}
                            </span>
                            {fieldEdit?.taskId === task.id &&
                              fieldEdit.field === "endDate" && (
                                <DashboardDuePopover
                                  task={task}
                                  openUpward={dropdownOpenUpward}
                                  isDarkMode={isDarkMode}
                                  accentColor={accentColor}
                                  cardBg={cardBg}
                                  borderColor={borderColor}
                                  textPrimary={textPrimary}
                                  textSecondary={textSecondary}
                                  onSave={(updates) =>
                                    updateTask(
                                      task.id,
                                      buildUpdateDto(task, updates),
                                    )
                                  }
                                  onClose={() => setFieldEdit(null)}
                                />
                              )}
                          </td>
                        );
                      }
                      if (key === "priority") {
                        // サブタスクには重要度を設定しない方針のためグレーアウト
                        if (isSubtask)
                          return (
                            <td
                              key="priority"
                              className="px-3 py-2 opacity-40 cursor-default"
                              title="サブタスクには重要度を設定できません"
                            >
                              <span
                                className="text-xs"
                                style={{ color: textSecondary }}
                              >
                                —
                              </span>
                            </td>
                          );
                        return (
                          <td
                            key="priority"
                            className="px-3 py-2 relative cursor-pointer hover:bg-black/5 transition-colors"
                            onClick={(e) => {
                              checkDropdownDirection(e);
                              setFieldEdit((prev) =>
                                prev?.taskId === task.id &&
                                prev.field === "priority"
                                  ? null
                                  : { taskId: task.id, field: "priority" },
                              );
                            }}
                          >
                            <span className="flex items-center gap-1 text-xs">
                              <span
                                className="w-2 h-2 rounded-full flex-shrink-0"
                                style={{
                                  backgroundColor:
                                    PRIORITY_CONFIG[task.priority]
                                      ?.circleColor ?? "#9E9E9E",
                                }}
                              />
                              <span style={{ color: textSecondary }}>
                                {task.priority !== "なし" ? task.priority : "—"}
                              </span>
                            </span>
                            {fieldEdit?.taskId === task.id &&
                              fieldEdit.field === "priority" && (
                                <div
                                  className={`absolute left-2 z-40 rounded-lg shadow-lg border overflow-hidden ${dropdownOpenUpward ? "bottom-full mb-1" : "top-8"}`}
                                  style={{
                                    background: cardBg,
                                    borderColor,
                                    minWidth: 80,
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {(
                                    [
                                      "高",
                                      "中",
                                      "低",
                                      "なし",
                                    ] as Task["priority"][]
                                  ).map((p) => (
                                    <button
                                      key={p}
                                      onClick={() => {
                                        updateTask(
                                          task.id,
                                          buildUpdateDto(task, { priority: p }),
                                        );
                                        setFieldEdit(null);
                                      }}
                                      className="flex items-center gap-2 w-full text-left px-3 py-1.5 text-xs hover:bg-black/5 transition-colors"
                                      style={{
                                        color: textPrimary,
                                        background:
                                          task.priority === p
                                            ? accentColor + "20"
                                            : undefined,
                                      }}
                                    >
                                      <span
                                        className="w-2 h-2 rounded-full flex-shrink-0"
                                        style={{
                                          backgroundColor:
                                            PRIORITY_CONFIG[p]?.circleColor ??
                                            "#9E9E9E",
                                        }}
                                      />
                                      {p}
                                    </button>
                                  ))}
                                </div>
                              )}
                          </td>
                        );
                      }
                      if (key === "tags") {
                        // サブタスクにはタグを設定しない方針のためグレーアウト
                        if (isSubtask)
                          return (
                            <td
                              key="tags"
                              className="px-3 py-2 opacity-40 cursor-default"
                              title="サブタスクにはタグを設定できません"
                            >
                              <span
                                className="text-xs"
                                style={{ color: textSecondary }}
                              >
                                —
                              </span>
                            </td>
                          );
                        return (
                          <td key="tags" className="px-3 py-2 relative">
                            <div
                              className="flex flex-wrap items-center gap-1 cursor-pointer min-h-[20px]"
                              onClick={(e) => {
                                checkDropdownDirection(e);
                                setFieldEdit((prev) =>
                                  prev?.taskId === task.id &&
                                  prev.field === "tags"
                                    ? null
                                    : { taskId: task.id, field: "tags" },
                                );
                              }}
                            >
                              {(task.tags || []).length > 0 ? (
                                task.tags.map((t) => (
                                  <span
                                    key={t.id}
                                    className="text-xs"
                                    style={{ color: t.color }}
                                  >
                                    #{t.name}
                                  </span>
                                ))
                              ) : (
                                <span
                                  className="text-xs"
                                  style={{ color: textSecondary }}
                                >
                                  —
                                </span>
                              )}
                            </div>
                            {fieldEdit?.taskId === task.id &&
                              fieldEdit.field === "tags" && (
                                <div
                                  className={`absolute left-2 z-40 rounded-lg shadow-lg border overflow-hidden ${dropdownOpenUpward ? "bottom-full mb-1" : "top-8"}`}
                                  style={{
                                    background: cardBg,
                                    borderColor,
                                    minWidth: 160,
                                    maxHeight: 200,
                                    overflowY: "auto",
                                  }}
                                >
                                  {availableTags.length === 0 ? (
                                    <div
                                      className="px-3 py-2 text-xs"
                                      style={{ color: textSecondary }}
                                    >
                                      タグがありません
                                    </div>
                                  ) : (
                                    availableTags.map((tag) => {
                                      const isSelected = (task.tags || []).some(
                                        (t) => t.id === tag.id,
                                      );
                                      const currentIds = new Set(
                                        (task.tags || []).map((t) => t.id),
                                      );
                                      return (
                                        <button
                                          key={tag.id}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (isSelected)
                                              currentIds.delete(tag.id);
                                            else currentIds.add(tag.id);
                                            updateTask(
                                              task.id,
                                              buildUpdateDto(task, {
                                                tagIds: Array.from(currentIds),
                                              }),
                                            );
                                          }}
                                          className="flex items-center gap-2 w-full text-left px-3 py-1.5 text-xs hover:bg-black/5 transition-colors"
                                          style={{
                                            background: isSelected
                                              ? tag.color + "18"
                                              : undefined,
                                          }}
                                        >
                                          <span style={{ color: tag.color }}>
                                            #{tag.name}
                                          </span>
                                          {isSelected && (
                                            <span
                                              className="ml-auto"
                                              style={{ color: accentColor }}
                                            >
                                              ✓
                                            </span>
                                          )}
                                        </button>
                                      );
                                    })
                                  )}
                                </div>
                              )}
                          </td>
                        );
                      }
                      if (key.startsWith("custom_")) {
                        const colId = parseInt(key.replace("custom_", ""));
                        const col = customColumns.find(
                          (c) => c.customColumnId === colId,
                        );
                        if (!col) return null;
                        const cellKey = `${task.id}_${colId}`;
                        const isEditing =
                          editingCell?.taskId === task.id &&
                          editingCell?.columnId === colId;
                        const val = getCellValue(task.id, colId);
                        const isLoading = loadingCellKey === cellKey;
                        return (
                          <td key={key} className="px-3 py-2">
                            {col.columnType === "text" ? (
                              isEditing ? (
                                <input
                                  ref={cellInputRef}
                                  className="w-full px-1 py-0.5 text-xs rounded border focus:outline-none"
                                  style={{
                                    borderColor: accentColor,
                                    background: isDarkMode ? "#2a2a2a" : "#fff",
                                    color: textPrimary,
                                    minWidth: 80,
                                  }}
                                  value={cellDraft}
                                  onChange={(e) => setCellDraft(e.target.value)}
                                  onBlur={commitCellEdit}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") commitCellEdit();
                                    if (e.key === "Escape")
                                      setEditingCell(null);
                                  }}
                                  maxLength={500}
                                />
                              ) : (
                                <button
                                  onClick={() => startCellEdit(task.id, colId)}
                                  className="w-full text-left px-1 py-0.5 text-xs rounded hover:bg-black/5 transition-colors min-h-[22px]"
                                  style={{
                                    color: val ? textPrimary : textSecondary,
                                    opacity: isLoading ? 0.5 : 1,
                                  }}
                                >
                                  {val || (
                                    <span className="text-gray-400">
                                      クリックして入力
                                    </span>
                                  )}
                                </button>
                              )
                            ) : (
                              <select
                                className="text-xs rounded px-1 py-0.5 border focus:outline-none"
                                style={{
                                  background: isDarkMode ? "#2a2a2a" : "#fff",
                                  color: textPrimary,
                                  borderColor,
                                  maxWidth: 140,
                                }}
                                value={val ?? ""}
                                onChange={async (e) => {
                                  await saveCellValue(
                                    task.id,
                                    colId,
                                    e.target.value,
                                  );
                                }}
                                disabled={isLoading}
                              >
                                <option value="">-</option>
                                {col.options.map((opt) => (
                                  <option
                                    key={opt.optionId}
                                    value={opt.optionValue}
                                  >
                                    {opt.optionValue}
                                  </option>
                                ))}
                              </select>
                            )}
                          </td>
                        );
                      }
                      return null;
                    })}

                    {/* 詳細アイコン + メニュー */}
                    <td className="px-1 py-2 relative">
                      <div className="flex items-center justify-end gap-0.5">
                        {/* 詳細を表示（アイコンのみ・常時表示） */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuTaskId(null);
                            setDetailTask(task);
                          }}
                          className="p-1 rounded-full hover:bg-black/10 transition-colors"
                          style={{ color: accentColor }}
                          title="詳細を表示"
                        >
                          <OpenInNewIcon style={{ fontSize: 16 }} />
                        </button>
                        <div
                          className="relative inline-block"
                          ref={
                            openMenuTaskId === task.id
                              ? menuPopoverRef
                              : undefined
                          }
                        >
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenMenuTaskId(
                                openMenuTaskId === task.id ? null : task.id,
                              );
                              setShowSubtaskTemplatesForTaskId(null);
                            }}
                            className="p-1 rounded-full hover:bg-black/10 transition-colors focus:opacity-100"
                            style={{ color: textSecondary }}
                            title="メニュー"
                          >
                            <RestaurantMenuIcon style={{ fontSize: 18 }} />
                          </button>
                          {openMenuTaskId === task.id && (
                            <div
                              className="absolute right-0 top-full mt-1 z-40 rounded-lg shadow-xl border py-1"
                              style={{
                                background: cardBg,
                                borderColor,
                                minWidth: 160,
                              }}
                            >
                              {!childTaskIds.has(task.id) && (
                                <>
                                  <button
                                    className="flex w-full items-center gap-2 px-3 py-2 text-xs text-left hover:bg-black/5 transition-colors"
                                    style={{ color: textPrimary }}
                                    onClick={() => {
                                      setOpenMenuTaskId(null);
                                      setAddingSubtaskForTaskId(task.id);
                                      setNewSubtaskTitle("");
                                      setTimeout(
                                        () => subtaskInputRef.current?.focus(),
                                        50,
                                      );
                                    }}
                                  >
                                    サブタスクを追加
                                  </button>
                                  <div className="relative">
                                    <button
                                      className="flex w-full items-center gap-2 px-3 py-2 text-xs text-left hover:bg-black/5 transition-colors"
                                      style={{ color: textPrimary }}
                                      onClick={() =>
                                        setShowSubtaskTemplatesForTaskId(
                                          showSubtaskTemplatesForTaskId ===
                                            task.id
                                            ? null
                                            : task.id,
                                        )
                                      }
                                    >
                                      テンプレートから追加
                                    </button>
                                    {showSubtaskTemplatesForTaskId ===
                                      task.id && (
                                      <div
                                        className="absolute right-full top-0 mr-1 z-50 rounded-lg shadow-xl border py-1"
                                        style={{
                                          background: cardBg,
                                          borderColor,
                                          minWidth: 200,
                                          maxHeight: 240,
                                          overflowY: "auto",
                                        }}
                                      >
                                        {subtaskTemplates.length === 0 ? (
                                          <div
                                            className="px-3 py-2 text-xs"
                                            style={{ color: textSecondary }}
                                          >
                                            テンプレートがありません
                                          </div>
                                        ) : (
                                          subtaskTemplates.map((tpl) => (
                                            <button
                                              key={tpl.templateId}
                                              className="flex w-full items-center gap-2 px-3 py-2 text-xs text-left hover:bg-black/5 transition-colors"
                                              style={{ color: textPrimary }}
                                              onClick={() =>
                                                handleApplySubtaskTemplate(
                                                  task.id,
                                                  tpl,
                                                )
                                              }
                                            >
                                              {tpl.templateName}
                                            </button>
                                          ))
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </>
                              )}
                              <button
                                className="flex w-full items-center gap-2 px-3 py-2 text-xs text-left hover:bg-red-50 transition-colors text-red-600"
                                onClick={() => {
                                  setOpenMenuTaskId(null);
                                  setConfirmDialog({
                                    isOpen: true,
                                    message: `タスク「${task.title}」を削除しますか？\nこの操作は元に戻せません。`,
                                    onConfirm: () => {
                                      setConfirmDialog((prev) => ({
                                        ...prev,
                                        isOpen: false,
                                      }));
                                      onDeleteTask(task.id);
                                    },
                                  });
                                }}
                              >
                                削除
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                  {addingSubtaskForTaskId === task.id && (
                    <tr
                      style={{
                        background: isDarkMode ? "#1e1e1e" : "#f9fafb",
                        borderBottom: `${borderW} solid ${borderColor}`,
                      }}
                    >
                      <td
                        className="pl-8 pr-3 py-1.5"
                        colSpan={activeColumnKeys.length + 2}
                      >
                        <div className="flex items-center gap-2">
                          <SubdirectoryArrowRightIcon
                            style={{
                              fontSize: 14,
                              color: textSecondary,
                              flexShrink: 0,
                            }}
                          />
                          <input
                            ref={subtaskInputRef}
                            type="text"
                            value={newSubtaskTitle}
                            onChange={(e) => setNewSubtaskTitle(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleAddSubtaskCommit(task.id);
                              }
                              if (e.key === "Escape") {
                                setAddingSubtaskForTaskId(null);
                                setNewSubtaskTitle("");
                              }
                            }}
                            onBlur={() => {
                              if (!newSubtaskTitle.trim()) {
                                setAddingSubtaskForTaskId(null);
                                setNewSubtaskTitle("");
                              }
                            }}
                            placeholder="サブタスクのタイトル（Enterで追加、Escでキャンセル）"
                            className="flex-1 text-xs bg-transparent outline-none"
                            style={{ color: textPrimary }}
                          />
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}

            {/* ========== 新規タスク作成行 ========== */}
            <tr
              className="sticky bottom-0 z-30"
              style={{
                background: cardBg,
                borderTop: `2px solid ${borderColor}`,
              }}
            >
              {/* タイトル入力 + プリセットボタン（全列をまたぐ1セル）。
                  列幅の影響を受けず、入力欄とプリセットボタンが常に同じ配置になる */}
              <td
                className="px-3 py-1.5"
                colSpan={activeColumnKeys.length + 2}
              >
                <div className="flex items-center gap-2">
                  <span
                    className="text-sm flex-shrink-0"
                    style={{ color: textSecondary }}
                  >
                    ＋
                  </span>
                  <input
                    ref={newTaskInputRef}
                    type="text"
                    value={newTaskTitle}
                    onChange={(e) => setNewTaskTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleCreateTask({ refocus: true });
                      }
                    }}
                    placeholder="タスクを新規作成"
                    className="text-sm bg-transparent outline-none"
                    // 幅は 260px を基本とし、プレースホルダー（タスクを新規作成）が
                    // 隠れないよう最小幅を確保する。これによりプリセットボタンは
                    // 常に入力欄の直後に一定間隔で並び、文字にかぶらない
                    style={{
                      color: textPrimary,
                      flex: "0 1 260px",
                      minWidth: 150,
                    }}
                    disabled={!activeListId || activeListId === "0"}
                  />

                  {/* プリセットボタン（入力欄の直隣・列幅に追従しない） */}
                  <div className="relative flex-shrink-0">
                    <button
                      onClick={() => {
                        setShowPresetDropdown((v) => !v);
                        setShowAddPresetForm(false);
                      }}
                      title="クイック作成プリセット"
                      className="px-2 py-1 rounded text-xs border transition-colors hover:opacity-80 whitespace-nowrap"
                      style={{
                        borderWidth: borderW,
                        borderColor:
                          showPresetDropdown || selectedPreset
                            ? accentColor
                            : borderColor,
                        color:
                          showPresetDropdown || selectedPreset
                            ? accentColor
                            : textSecondary,
                        background:
                          showPresetDropdown || selectedPreset
                            ? accentColor + "12"
                            : "transparent",
                      }}
                    >
                      {selectedPreset
                        ? `✓ ${selectedPreset.name}`
                        : "プリセットを設定・使用 ▾"}
                    </button>

                    {showPresetDropdown && (
                      <div
                        className="absolute left-0 bottom-full mb-1 z-40 rounded-lg shadow-xl border overflow-hidden"
                        style={{
                          background: cardBg,
                          borderColor,
                          minWidth: 230,
                          maxWidth: 300,
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {/* プリセット一覧 */}
                        <div
                          className="max-h-48 overflow-y-auto"
                          style={{
                            borderBottom:
                              presets.length > 0 || showAddPresetForm
                                ? `${borderW} solid ${borderColor}`
                                : "none",
                          }}
                        >
                          {presets.length === 0 && !showAddPresetForm && (
                            <div
                              className="px-4 py-3 text-xs"
                              style={{ color: textSecondary }}
                            >
                              プリセットがありません
                            </div>
                          )}
                          {presets.map((preset) => {
                            const isSelected = selectedPreset?.id === preset.id;
                            const presetTags = availableTags.filter((t) =>
                              preset.tagIds.includes(t.id),
                            );
                            return (
                              <div
                                key={preset.id}
                                className="flex items-center justify-between px-3 py-2 hover:bg-black/5 cursor-pointer transition-colors"
                                style={{
                                  background: isSelected
                                    ? accentColor + "14"
                                    : undefined,
                                }}
                                onClick={() => {
                                  handleCreateTaskFromPreset(preset);
                                }}
                              >
                                <div className="flex-1 min-w-0">
                                  <div
                                    className="text-xs font-medium truncate"
                                    style={{
                                      color: isSelected
                                        ? accentColor
                                        : textPrimary,
                                    }}
                                  >
                                    {isSelected && (
                                      <span className="mr-1">✓</span>
                                    )}
                                    {preset.name}
                                  </div>
                                  <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                    {preset.status !== "未対応" && (
                                      <span
                                        className="text-xs"
                                        style={{
                                          color: getStatusColor(preset.status),
                                        }}
                                      >
                                        {preset.status}
                                      </span>
                                    )}
                                    {preset.priority !== "なし" && (
                                      <span
                                        className="text-xs"
                                        style={{
                                          color:
                                            PRIORITY_CONFIG[preset.priority]
                                              ?.circleColor,
                                        }}
                                      >
                                        {preset.priority}
                                      </span>
                                    )}
                                    {presetTags.map((t) => (
                                      <span
                                        key={t.id}
                                        className="text-xs"
                                        style={{ color: t.color }}
                                      >
                                        #{t.name}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                                {(!preset.createdHonbuId ||
                                  preset.createdHonbuId === honbuId) && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeletePreset(preset.id);
                                    }}
                                    className="ml-2 text-xs opacity-40 hover:opacity-80 flex-shrink-0"
                                    style={{ color: textSecondary }}
                                  >
                                    ✕
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        {/* 追加フォーム */}
                        {showAddPresetForm ? (
                          <div className="px-3 py-2 space-y-2">
                            <input
                              autoFocus
                              type="text"
                              placeholder="プリセット名"
                              value={draftPreset.name}
                              onChange={(e) =>
                                setDraftPreset((d) => ({
                                  ...d,
                                  name: e.target.value,
                                }))
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter") handleSavePreset();
                                if (e.key === "Escape")
                                  setShowAddPresetForm(false);
                              }}
                              className="w-full text-xs px-2 py-1 rounded border focus:outline-none"
                              style={{
                                borderColor: accentColor,
                                background: isDarkMode ? "#2a2a2a" : "#fff",
                                color: textPrimary,
                              }}
                            />
                            <div className="flex gap-2">
                              <select
                                value={draftPreset.status}
                                onChange={(e) =>
                                  setDraftPreset((d) => ({
                                    ...d,
                                    status: e.target.value,
                                  }))
                                }
                                className="flex-1 text-xs px-1 py-1 rounded border focus:outline-none"
                                style={{
                                  background: isDarkMode ? "#2a2a2a" : "#fff",
                                  color: textPrimary,
                                  borderColor,
                                }}
                              >
                                {allStatuses.map((s) => (
                                  <option key={s} value={s}>
                                    {s}
                                  </option>
                                ))}
                              </select>
                              <select
                                value={draftPreset.priority}
                                onChange={(e) =>
                                  setDraftPreset((d) => ({
                                    ...d,
                                    priority: e.target
                                      .value as Task["priority"],
                                  }))
                                }
                                className="flex-1 text-xs px-1 py-1 rounded border focus:outline-none"
                                style={{
                                  background: isDarkMode ? "#2a2a2a" : "#fff",
                                  color: textPrimary,
                                  borderColor,
                                }}
                              >
                                {(
                                  [
                                    "なし",
                                    "低",
                                    "中",
                                    "高",
                                  ] as Task["priority"][]
                                ).map((p) => (
                                  <option key={p} value={p}>
                                    {p}
                                  </option>
                                ))}
                              </select>
                            </div>
                            {availableTags.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {availableTags.map((tag) => {
                                  const checked = draftPreset.tagIds.includes(
                                    tag.id,
                                  );
                                  return (
                                    <button
                                      key={tag.id}
                                      onClick={() =>
                                        setDraftPreset((d) => ({
                                          ...d,
                                          tagIds: checked
                                            ? d.tagIds.filter(
                                                (id) => id !== tag.id,
                                              )
                                            : [...d.tagIds, tag.id],
                                        }))
                                      }
                                      className="text-xs px-2 py-0.5 rounded-full border transition-colors"
                                      style={{
                                        borderColor: checked
                                          ? tag.color
                                          : borderColor,
                                        color: checked
                                          ? tag.color
                                          : textSecondary,
                                        background: checked
                                          ? tag.color + "18"
                                          : "transparent",
                                      }}
                                    >
                                      #{tag.name}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                            <div className="flex gap-2 justify-end">
                              <button
                                onClick={() => setShowAddPresetForm(false)}
                                className="text-xs px-2 py-1 rounded"
                                style={{ color: textSecondary }}
                              >
                                キャンセル
                              </button>
                              <button
                                onClick={handleSavePreset}
                                className="text-xs px-3 py-1 rounded"
                                style={{
                                  background: accentColor,
                                  color: "#fff",
                                }}
                              >
                                保存
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => setShowAddPresetForm(true)}
                            className="w-full text-left px-3 py-2 text-xs hover:bg-black/5 transition-colors"
                            style={{ color: accentColor }}
                          >
                            ＋ プリセットを追加
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ========== 列設定バー（テーブル下） ========== */}
      <div
        className="flex-shrink-0 flex items-center gap-1 px-4 py-2 text-xs"
        style={{ borderTop: `${borderW} solid ${borderColor}`, color: textSecondary }}
      >
        <span>列設定:</span>
        <button
          onClick={() => handleColumnModeChange("common")}
          className="flex items-center gap-0.5 px-2 py-0.5 rounded transition-colors"
          style={
            columnMode === "common"
              ? { color: accentColor, background: accentColor + "18" }
              : {}
          }
        >
          <PeopleOutlineIcon style={{ fontSize: 13 }} />
          共通
        </button>
        <button
          onClick={() => handleColumnModeChange("personal")}
          className="flex items-center gap-0.5 px-2 py-0.5 rounded transition-colors"
          style={
            columnMode === "personal"
              ? { color: accentColor, background: accentColor + "18" }
              : {}
          }
        >
          <PersonOutlineIcon style={{ fontSize: 13 }} />
          個人
        </button>
        <button
          onClick={() => setShowColumnSettingsDialog(true)}
          className="flex items-center gap-1 px-2 py-0.5 rounded border transition-colors hover:opacity-80"
          style={{ borderColor, borderWidth: borderW, color: textSecondary }}
        >
          列の表示設定
        </button>
        <span
          className="mx-1"
          style={{
            borderLeft: `${borderW} solid ${borderColor}`,
            height: 14,
            display: "inline-block",
          }}
        />
        <button
          onClick={() => setShowCsvImportDialog(true)}
          disabled={!activeListId}
          title="CSV / Excelファイルからタスクを一括取り込み"
          className="flex items-center gap-1 px-2 py-0.5 rounded border transition-colors hover:opacity-80"
          style={{
            borderColor,
            borderWidth: borderW,
            color: textSecondary,
            opacity: !activeListId ? 0.5 : 1,
          }}
        >
          <FileUploadOutlinedIcon style={{ fontSize: 14 }} />
          CSV/Excelインポート
        </button>
      </div>

      {/* ドロップダウン外クリックで閉じる */}
      {(editingStatusTaskId ||
        fieldEdit?.field === "priority" ||
        fieldEdit?.field === "tags" ||
        showPresetDropdown) && (
        <div
          className="fixed inset-0 z-20"
          onClick={() => {
            setEditingStatusTaskId(null);
            if (fieldEdit?.field === "priority" || fieldEdit?.field === "tags")
              setFieldEdit(null);
            setShowPresetDropdown(false);
          }}
        />
      )}

      {/* 担当者選択ダイアログ */}
      {memberDialogTask && (
        <MemberSelectionDialog
          roomMembers={roomMembers}
          initialSelected={(memberDialogTask.assignees || []).map(
            (u) => u.name,
          )}
          onSave={handleAssigneeSave}
          onClose={() => setMemberDialogTask(null)}
          dialogType="assignees"
        />
      )}

      {/* カスタム列ダイアログ */}
      {showColumnDialog && activeListId && (
        <DashboardCustomColumnDialog
          columns={customColumns}
          listId={activeListId}
          honbuId={honbuId}
          color={color}
          isDarkMode={isDarkMode}
          onClose={() => setShowColumnDialog(false)}
          onColumnsChange={(updated) => {
            setCustomColumns(updated);
          }}
        />
      )}

      {/* 列表示設定ダイアログ */}
      {showColumnSettingsDialog && activeListId && (
        <DashboardColumnSettingsDialog
          listId={activeListId}
          honbuId={honbuId}
          roomId={roomId}
          customColumns={customColumns}
          color={color}
          isDarkMode={isDarkMode}
          onClose={handleColumnSettingsSaved}
        />
      )}

      {/* CSVインポートダイアログ */}
      {showCsvImportDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => {
              if (!importLoading) setShowCsvImportDialog(false);
            }}
          />
          <div
            className="relative rounded-xl shadow-2xl overflow-hidden"
            style={{
              background: cardBg,
              border: `2px solid ${isDarkMode ? "#000" : "#fff"}`,
              minWidth: 340,
              maxWidth: 480,
              width: "90%",
              maxHeight: "85vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* ヘッダー */}
            <div
              className="flex items-center justify-between px-4 py-3"
              style={{
                background: `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)`,
              }}
            >
              <span className="text-sm font-semibold text-white">
                CSV / Excelインポート
              </span>
              <button
                onClick={() => setShowCsvImportDialog(false)}
                disabled={importLoading}
                className="text-white/80 hover:text-white"
              >
                <CloseIcon style={{ fontSize: 18 }} />
              </button>
            </div>

            {/* ボディ */}
            <div className="overflow-y-auto px-5 py-4 flex flex-col gap-4">
              {/* 機能説明 */}
              <div>
                <p
                  className="text-xs leading-relaxed mb-1.5"
                  style={{ color: textPrimary }}
                >
                  特定形式のCSVまたはExcel（.xlsx）ファイルを読み込ませることで、タスクを一括作成できます。
                </p>
                <ul
                  className="list-disc pl-4 text-xs leading-relaxed flex flex-col gap-0.5"
                  style={{ color: textSecondary }}
                >
                  <li>
                    タスクの項目に適したヘッダー名（1行目）が必要です。テンプレートのヘッダー名は変更しないでください。
                  </li>
                  <li>
                    1行目（ヘッダー行）と「例:」から始まる行は取り込み対象外です。
                  </li>
                  <li>
                    列の並べ替え・不要な列の削除は可能です（「タイトル（必須）」列は削除できません）。
                  </li>
                  <li>
                    ヘッダー名の括弧書きは省略できます（例:「タイトル（必須）」→「タイトル」、「開始日(YYYY-MM-DD)」→「開始日」）。
                  </li>
                </ul>
              </div>

              {/* STEP 1 */}
              <div className="flex items-start gap-3">
                <span
                  className="flex-shrink-0 flex items-center justify-center rounded-full font-bold text-white"
                  style={{
                    width: 22,
                    height: 22,
                    fontSize: 12,
                    background: accentColor,
                  }}
                >
                  1
                </span>
                <div className="flex-1 flex flex-col gap-1">
                  <span
                    className="text-xs font-semibold"
                    style={{ color: textPrimary }}
                  >
                    テンプレートCSVをダウンロード
                  </span>
                  <span style={{ fontSize: 11, color: textSecondary }}>
                    このプロジェクトの項目に合わせたヘッダー付きCSVファイルをダウンロードし、タスクを入力してください。
                  </span>
                  <label className="flex items-center gap-1 cursor-pointer mt-1 self-start">
                    <Switch
                      size="small"
                      checked={splitPeriod}
                      onChange={(e) => setSplitPeriod(e.target.checked)}
                    />
                    <span
                      className="text-xs font-medium"
                      style={{ color: textPrimary }}
                    >
                      期限を期間（開始日・終了日）に分ける
                    </span>
                  </label>
                  <span style={{ fontSize: 10.5, color: textSecondary }}>
                    {splitPeriod
                      ? "開始日と終了日の2列で入力します。"
                      : "期限1列で入力します（記入した日付が開始日・終了日の両方に登録され、期間なしの状態になります）。"}
                  </span>
                  <button
                    onClick={handleDownloadTemplate}
                    className="self-start flex items-center gap-1 text-xs px-3 py-1.5 mt-1 rounded border transition-colors hover:opacity-80"
                    style={{ borderColor: accentColor, color: accentColor }}
                  >
                    <FileDownloadOutlinedIcon style={{ fontSize: 15 }} />
                    テンプレートをダウンロード
                  </button>
                </div>
              </div>

              {/* STEP 2 */}
              <div className="flex items-start gap-3">
                <span
                  className="flex-shrink-0 flex items-center justify-center rounded-full font-bold text-white"
                  style={{
                    width: 22,
                    height: 22,
                    fontSize: 12,
                    background: accentColor,
                  }}
                >
                  2
                </span>
                <div className="flex-1 flex flex-col gap-1">
                  <span
                    className="text-xs font-semibold"
                    style={{ color: textPrimary }}
                  >
                    CSV / Excelファイルを読み込む
                  </span>
                  <span style={{ fontSize: 11, color: textSecondary }}>
                    入力済みのCSVまたはExcel（.xlsx）ファイルを選択すると、タスクが一括作成されます。Excelは1枚目のシートが取り込まれます。
                  </span>
                  <label
                    className="self-start flex items-center gap-1 text-xs px-3 py-1.5 mt-1 rounded cursor-pointer transition-colors hover:opacity-90"
                    style={{
                      background: accentColor,
                      color: "#fff",
                      opacity: importLoading ? 0.5 : 1,
                      pointerEvents: importLoading ? "none" : "auto",
                    }}
                  >
                    <FileUploadOutlinedIcon style={{ fontSize: 15 }} />
                    {importLoading ? "取込中..." : "ファイルを読み込む"}
                    <input
                      ref={csvFileInputRef}
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      className="hidden"
                      onChange={handleImportFileChange}
                    />
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSVインポート結果ダイアログ */}
      {importResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setImportResult(null)}
          />
          <div
            className="relative rounded-xl shadow-2xl overflow-hidden"
            style={{
              background: cardBg,
              border: `2px solid ${isDarkMode ? "#000" : "#fff"}`,
              minWidth: 340,
              maxWidth: 520,
              width: "90%",
              maxHeight: "80vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* ヘッダー */}
            <div
              className="flex items-center justify-between px-4 py-3"
              style={{
                background: `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)`,
              }}
            >
              <span className="text-sm font-semibold text-white">
                インポート結果
              </span>
              <button
                onClick={() => setImportResult(null)}
                className="text-white/80 hover:text-white"
              >
                <CloseIcon style={{ fontSize: 18 }} />
              </button>
            </div>

            {/* 集計 */}
            <div
              className="flex gap-4 px-4 py-3"
              style={{ borderBottom: `${borderW} solid ${borderColor}` }}
            >
              <div className="flex items-center gap-1.5 text-sm">
                <CheckIcon style={{ fontSize: 16, color: "#40C063" }} />
                <span style={{ color: "#40C063" }} className="font-semibold">
                  {importResult.successCount}
                </span>
                <span style={{ color: textSecondary }}>件作成</span>
              </div>
              {importResult.errorCount > 0 && (
                <div className="flex items-center gap-1.5 text-sm">
                  <CloseIcon style={{ fontSize: 16, color: "#EF4444" }} />
                  <span style={{ color: "#EF4444" }} className="font-semibold">
                    {importResult.errorCount}
                  </span>
                  <span style={{ color: textSecondary }}>件エラー</span>
                </div>
              )}
              {importResult.warningCount > 0 && (
                <div className="flex items-center gap-1.5 text-sm">
                  <WarningAmberIcon
                    style={{ fontSize: 16, color: "#F59E0B" }}
                  />
                  <span style={{ color: "#F59E0B" }} className="font-semibold">
                    {importResult.warningCount}
                  </span>
                  <span style={{ color: textSecondary }}>件警告</span>
                </div>
              )}
            </div>

            {/* エラー・警告一覧 */}
            {importResult.errors.length > 0 && (
              <div className="overflow-y-auto flex-1 px-4 py-2">
                {importResult.errors.map((e, i) => {
                  const isWarning = e.message.startsWith("[警告]");
                  return (
                    <div
                      key={i}
                      className="flex gap-2 py-1.5 text-xs"
                      style={{ borderBottom: `${borderW} solid ${borderColor}` }}
                    >
                      <span
                        className="flex-shrink-0 font-medium"
                        style={{
                          color: isWarning ? "#F59E0B" : "#EF4444",
                          minWidth: 48,
                        }}
                      >
                        {e.row > 0 ? `行${e.row}` : isWarning ? "列" : ""}
                      </span>
                      <span style={{ color: textPrimary }}>{e.message}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* フッター */}
            <div
              className="flex justify-end px-4 py-3"
              style={{ borderTop: `${borderW} solid ${borderColor}` }}
            >
              <button
                onClick={() => setImportResult(null)}
                className="text-xs px-4 py-1.5 rounded"
                style={{ background: accentColor, color: "#fff" }}
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 削除確認ダイアログ */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() =>
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }))
        }
      />

      {/* タスク詳細パネル（オーバーレイ）：ピン留め時はテーブル横に表示するためここでは出さない */}
      {detailTask && !isDetailPinned && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex justify-end"
          onClick={() => setDetailTask(null)}
        >
          <div
            className={`relative h-full shadow-lg animate-slide-in-right ${isDarkMode ? "bg-gray-900" : "bg-white"}`}
            style={{ width: detailPanelWidth }}
            onClick={(e) => e.stopPropagation()}
          >
            <PanelResizeHandle
              onPointerDown={handleDetailResizeStart}
              isDarkMode={isDarkMode}
            />
            {detailPanel}
          </div>
        </div>
      )}

      {/* タスク詳細パネル（ピン留め固定表示）。
          transform を持つメインエリアラッパーが fixed の基点になるため、
          right:0 = ヘルプパネルの左端、top:0 = ショートカットヘッダーの下に収まる。 */}
      {showPinnedPanel && (
        <div
          className="fixed top-0 right-0 bottom-0 shadow-2xl animate-slide-in-right"
          style={{
            width: detailPanelWidth,
            zIndex: 1400,
            background: cardBg,
            borderLeft: `${borderW} solid ${borderColor}`,
          }}
        >
          <PanelResizeHandle
            onPointerDown={handleDetailResizeStart}
            isDarkMode={isDarkMode}
          />
          {detailPanel}
        </div>
      )}
    </div>
  );
};
